/**
 * Layer 1, first rung (WORKBOARD lane D): squads under orders over capture points.
 *
 * A team's units are partitioned into fixed squads; a **commander** (a deterministic policy
 * over the match state, re-planned on a cadence) gives each squad one order — take a point,
 * hold a point, escort a unit, or free (the uncommanded bot). The action bot obeys an order
 * exactly where it would otherwise pick a point for itself: when nothing is in sight. A
 * visible enemy still wins — orders shape where the fight happens, not whether a bot shoots.
 *
 * An **order ledger** watches the state and scores every order: completed (the squad got
 * there / the point fell), died first, or superseded by a re-plan. The harness reports these
 * so "does commanding beat not commanding, and by how much per order type" is a number.
 *
 * Orders are derived from state here (a bot commander). In the game they become inputs
 * from the leadership panel crossing the host — the same records.
 */
import { targetsLeft, storyPoints, escortRiders } from '../core/storyObjectives';   // 69.50 step 1; 69.61; DECISIONS 298 (e)
import { knownStructure, knownBase, baseArea } from '../core/intelRecord';   // 69.73 step 2b, 3
import { STORY, STICKY_TAKE } from '../constants/action';
import { airstrikeTarget } from './airstrikeRule';
import { cutOffSquads, dropshipCall, focusPoint, gunshipCall, massing, podCall, pickupCall, rescueCall, strandedSquads, supplyDropCall } from './dropRules';
import { dropshipsLeft } from '../core/dropship';
import { rescueLive } from '../core/rescue';
import { mountedForce, mountedReady } from '../core/plays';
import { crewWanted } from './technicalLoop';
import { planEfforts, effortsOn, type EffortSpec } from './efforts';
import { activeSites, sectorAt } from '../core/capturePoints';
import { knownObjectives, sitesAsKnown, roughRadius } from '../core/objectiveIntel';
import { reserveView } from '../core/reserves';
import { isTactic, tacticComplete, type TacticOrder } from '../core/tactics';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { rideArrived, RIDE_ARRIVE } from '../core/ride';
import { orderPlace } from '../core/orderForms';
import { squadClaimRank, squadOf, squadOrderFor, squadOrderSourceFor, squadTaskFor, taskRank, TASK_RANK } from '../core/squads';
import { scoutReady, pullUnordered } from './frontPull';
import { flankOn } from '../core/flankRoute';
import { createStandoffPlanner, standoffOn } from './standoffManoeuvre';
import { createAmbushPlanner, ambushOn, type AmbushRecord } from './skills/ambush';   // 416
import { createRallyPlanner, rallyOn, type RallyRecord } from './skills/rally';   // 416: the rally
import { holdingAndPushing, holdsWhilePushing } from './holdAndPush';
import { sideRead, type SideRead } from './sideRead';
import { linkedIds } from '../core/web';
import { relayLink } from '../core/relay';
import { buildRefusal, frontSiteKind, sectorInPlay } from '../core/build';
import { SUPPORT_POSITION, rifleEffectivePeople } from '../core/support';
import { PHYS_DT, FOB, INTEL } from '../constants';
import { fobsOn, fobsLeftFor } from '../core/fob';
import { structureTeam, seatArmed, drives } from '../core/body';
import { gunSupportPosition, gunnerEyePeople } from '../core/gunSupport';
import { brainFace } from '../core/brainFace';
import { vehiclesBarred } from '../core/vehicleUse';
import { isTransport } from '../core/commandTable';
import { convoysOn, planConvoys } from '../core/convoys';
import { planHmg } from '../core/hmgErrand';
import type { CapturePoint, GameState, PlayerState, Vec3 } from '../types';
import { vecDot, vecNormalize, vecSub, vecLength, vecAdd, vecScale, surfaceTangentFrame } from '../math';
import { dacos, dcos, dsin } from '../core/dmath';

/** The order type lives in types.ts (R11: the order input names it); re-exported for this lane's callers. */
import type { SkillTag, SquadOrder, TaskClaim } from '../types';
import { storesShort } from './rearmErrand';
import { strikeRestock } from '../core/grenadeStock';
import { crewSeats } from '../core/baseCrew';
import { countOf, defaultOn } from '../core/defaultOn';
export type { SquadOrder };

export type CommanderStyle = 'none' | 'spread' | 'focus' | 'adaptive';
export const COMMANDER_STYLES: CommanderStyle[] = ['none', 'spread', 'focus', 'adaptive'];

/** A commander's squad: `match`, one of the Match's own (69.87, its id the Match's), else its own fixed partition's. */
export interface Squad { id: number; team: number; unitIds: number[]; match?: true; /** 69.87: a drop squad (MatchSquad.drop) */ drop?: true }

/**
 * 69.87 (the coordinator, Oct 26, from lane F's drift measure; GameConfig.commanderSquads): the commander plans over the
 * Match's squads — on by default on the action clock, off its fixed partition by unit id (`squadsOf`), which disagreed with the
 * battle's layout (squad types of 3–5) and with 68.10's fluid squads: 70 % of lab-38's orders landed on a squad most of whose
 * people were in another.
 */
export function commanderSquadsOn(config: Pick<GameState['config'], 'commanderSquads' | 'clock'> | undefined): boolean {
  return config?.commanderSquads === true || (config?.clock === 'action' && config.commanderSquads !== false);
}

/** 69.87: a team's Match squads as the commander's, by id. */
export function matchSquadsOf(state: Pick<GameState, 'squads'>, team: number): Squad[] {
  return (state.squads ?? []).filter(q => q.team === team).sort((a, b) => a.id - b.id).map(q => ({ id: q.id, team, unitIds: q.unitIds, match: true as const, ...(q.drop ? { drop: true as const } : {}) }));
}

/** Seconds between re-plans. */
export const REPLAN_SECONDS = 10;
/**
 * 69.6c (lane C's Foothold diagnostic: the attackers fight in twos — a median 2 of 9 in contact at once, 62 % of their
 * deaths with no other attacking squad in contact, trading 1.38 alone v 1.91 together): under an Operations push the
 * squads sent at the active sector converge — a squad within `stagePeople` (+ `readyPeople`) of its point, or in contact,
 * is ready; while others are still coming, the ready ones hold `stagePeople` short of their points (out of the rifle's
 * reach of them); all ready, or `maxSeconds` after the staging began, they go together. Contact is the team's picture.
 */
export const CONVERGE = { stagePeople: 45, readyPeople: 10, contactPeople: 25, maxSeconds: 60 };
/** 69.6c: after a play dissolved in its quiet setting, no play of ours at that objective for this long (s). */
export const PLAY_QUIET_REFORM_SECONDS = 120;
/**
 * 69.6c lever 4 (the owner: "Pretty easy to win with a 16 man team of even bots on medium"): under an Operations push, a
 * point of ours the enemy is taking or has just taken is counter-attacked at once — the commander re-plans that tick (not at its next 10 s cadence:
 * a push's sector fell 2–18 s after its first objective), and its `squads` nearest squads not holding another point are
 * sent to take it back, for `seconds` or until it is ours again.
 */
export const COUNTER_ATTACK = { squads: 2, seconds: 45 };
/**
 * 68.25 (the owner: "if there's a stalemate if a bot could realize there's an unused apc nearby with a 50 cal and bring
 * it up on a ridgeline"): a point the plan sends us to, not ours, with one of us within `fightPeople` of it this long (s),
 * is a stalemate (20 s: at 60 it never fired on Foothold, the points falling within ~50 s); the two of ours on foot nearest
 * an idle APC of the side, within `crewPeople` of it, are sent to ride it to the .50's position (core/gunSupport.ts) with
 * `keep: 'crew'` — the driver and the gunner stay up there, the base of fire; the ride a task that lapses after `seconds`.
 * One a point for each stalemate, one at a time.
 */
export const GUN_SUPPORT_PLAN = { stalemateSeconds: 20, fightPeople: 40, crewPeople: 80, seconds: 150 };
/** Within this arc distance (u) of a point a squad has arrived. */
export const ARRIVE_ARC = 1.5;
/** An escort stays within this arc distance (u) of its charge. */
export const ESCORT_ARC = 2.0;
/** An owned point with an enemy within this arc (u) and no friend within ARRIVE_ARC is threatened. */
export const THREAT_ARC = 6.0;

/**
 * The commander's distances in person units, through the scale (the commander sweep, Oct 11: at the human scale the
 * classic numbers read 27 people as "arrived" against a 2.7-person capture radius, and a threat 109 people off as on the
 * point). Each arc above is a classic distance; here it is × the scale's k, so the classic game is unchanged and at
 * person scale each keeps its ratio to the capture radius (arrived ½ of it, threat 2×), as the rest of L0 does.
 */
export function commanderArcs(state: Pick<GameState, 'config'>): { arrive: number; escort: number; threat: number } {
  const k = personSetOf(state as GameState).k;
  return { arrive: ARRIVE_ARC * k, escort: ESCORT_ARC * k, threat: THREAT_ARC * k };
}

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
export function arcBetween(a: Vec3, b: Vec3, radius: number): number {
  return dacos(clamp(vecDot(vecNormalize(a), vecNormalize(b)), -1, 1)) * radius;
}

/** A team's units in fixed squads of `size`, by unit id (deterministic; dead units keep their place and respawn into it). */
export function squadsOf(state: Pick<GameState, 'players'>, team: number, size: number): Squad[] {
  const ids = state.players.filter(p => p.teamIndex === team).map(p => p.id).sort((a, b) => a - b);
  const out: Squad[] = [];
  const step = Math.max(1, Math.floor(size) || 1);   // (a size under one makes squads of one — it used to loop forever)
  for (let i = 0; i < ids.length; i += step) out.push({ id: out.length, team, unitIds: ids.slice(i, i + step) });
  return out;
}

function alive(state: GameState, squad: Squad): PlayerState[] {
  return squad.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p && p.alive);
}

/** Where a squad is: the mean direction of its living units, or null if none live. */
function squadDir(state: GameState, squad: Squad): Vec3 | null {
  const us = alive(state, squad);
  if (us.length === 0) return null;
  let x = 0, y = 0, z = 0;
  for (const u of us) { x += u.surfaceDir.x; y += u.surfaceDir.y; z += u.surfaceDir.z; }
  const v = { x, y, z };
  return vecLength(v) < 1e-9 ? us[0].surfaceDir : vecNormalize(v);
}

/**
 * 73.8 (1): where a squad is, for the way to the point it takes — the mean direction of its living members on foot away from
 * its side's base (outside STICKY_TAKE.basePeople of `home`): a member respawned at the base, or crewing a vehicle, does not
 * move it. Null with none such (the caller reads squadDir).
 */
function takeDir(state: GameState, squad: Squad, home: Vec3 | null, radius: number): Vec3 | null {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const us = alive(state, squad).filter(u => !u.mount && !(home && arcBetween(u.surfaceDir, home, radius) <= STICKY_TAKE.basePeople * person));
  if (us.length === 0) return null;
  let x = 0, y = 0, z = 0;
  for (const u of us) { x += u.surfaceDir.x; y += u.surfaceDir.y; z += u.surfaceDir.z; }
  const v = { x, y, z };
  return vecLength(v) < 1e-9 ? us[0].surfaceDir : vecNormalize(v);
}

/**
 * The commander's orders for one team right now. Pure in the state; same state → same orders.
 * 69.5a (decision 145 (2), the same rule for both commanders): it plans on the team's knowledge of the points
 * (core/objectiveIntel.ts, the one accessor the table reads too) — with objectives known roughly (`roughStart`) a point
 * not yet resolved stands at its circle's centre and nobody's as far as the side knows, and a squad sent to take it is
 * sent to scout its circle instead (69.2d's scout); once the circle resolves, the next plan sends the take. Off, the
 * points as they are (nothing moves).
 */
export function planOrders(state: GameState, team: number, squads: Squad[], style: CommanderStyle, counter: readonly number[] = [], tick = 0, side: SideRead = sideRead(state, team, tick)): Map<number, SquadOrder> {
  const orders = new Map<number, SquadOrder>();
  const sites = sitesAsKnown(state, team, activeSites(state), tick);   // 69.6d: under a push, the active sector's only; 69.5a: as the side knows them
  if (style === 'none' || sites.length === 0) { for (const s of squads) orders.set(s.id, { kind: 'free' }); return orders; }
  const planetIndex = sites[0].planetIndex;
  const radius = state.planets[planetIndex].radius;
  // (182 (3), commanderPicture: where its side believes the enemy is — the team picture — not the truth)
  const enemies: Array<{ surfaceDir: Vec3 }> = state.config.commanderPicture !== false
    ? side.picture().filter(x => x.planetIndex === planetIndex)
    : state.players.filter(p => p.alive && p.teamIndex !== team && p.planetIndex === planetIndex);
  const friends = state.players.filter(p => p.alive && p.teamIndex === team && p.planetIndex === planetIndex);
  const live = squads.map(s => ({ squad: s, dir: squadDir(state, s), n: alive(state, s).length })).filter(x => x.dir !== null) as Array<{ squad: Squad; dir: Vec3; n: number }>;
  const wanted = sites.filter(s => s.owner !== team || (s.capturer !== null && s.capturer !== team));
  const owned = sites.filter(s => s.owner === team);
  const arcs = commanderArcs(state);
  const threatened = owned.filter(s => enemies.some(e => arcBetween(e.surfaceDir, s.surfaceDir, radius) < arcs.threat) && !friends.some(f => arcBetween(f.surfaceDir, s.surfaceDir, radius) < arcs.arrive));
  const assigned = new Set<number>();
  const nearestSquad = (dir: Vec3) => {
    let best: { squad: Squad; d: number } | null = null;
    for (const x of live) { if (assigned.has(x.squad.id)) continue; const d = arcBetween(x.dir, dir, radius); if (!best || d < best.d) best = { squad: x.squad, d }; }
    return best?.squad ?? null;
  };
  const give = (squad: Squad, order: SquadOrder) => { orders.set(squad.id, order); assigned.add(squad.id); };
  // 73.8 (1): the squad whose base order already takes or holds a point keeps it — a rule giving each point its nearest squad
  // re-dealt them as they walked (s71-9: the story objectives' squads swapped six times before arriving); else the nearest
  const keeperOr = (site: CapturePoint): Squad | null => {
    const k = live.find(x => !assigned.has(x.squad.id) && x.squad.match && (() => { const b = state.squads?.find(m => m.id === x.squad.id)?.base; return (b?.kind === 'take' || b?.kind === 'hold') && b.pointId === site.id; })());
    return k?.squad ?? nearestSquad(site.surfaceDir);
  };

  if (style === 'spread') {
    // one squad per point we do not hold, nearest squad first; the rest guard held points
    for (const site of [...wanted].sort((a, b) => a.id - b.id)) { const q = nearestSquad(site.surfaceDir); if (q) give(q, { kind: 'take', pointId: site.id }); }
    for (const site of [...owned].sort((a, b) => a.id - b.id)) { const q = nearestSquad(site.surfaceDir); if (q) give(q, { kind: 'hold', pointId: site.id }); }
  } else if (style === 'focus') {
    // everyone on one point: the wanted point nearest the team's centre; with everything held, hold the most threatened
    let cx = 0, cy = 0, cz = 0;
    for (const x of live) { cx += x.dir.x; cy += x.dir.y; cz += x.dir.z; }
    const centre = vecLength({ x: cx, y: cy, z: cz }) < 1e-9 ? (live[0]?.dir ?? sites[0].surfaceDir) : vecNormalize({ x: cx, y: cy, z: cz });
    const pool = wanted.length > 0 ? wanted : (threatened.length > 0 ? threatened : owned);
    let target: CapturePoint | null = null, bestD = Infinity;
    for (const s of pool) { const d = arcBetween(s.surfaceDir, centre, radius); if (d < bestD) { bestD = d; target = s; } }
    if (target) for (const x of live) give(x.squad, wanted.length > 0 ? { kind: 'take', pointId: target.id } : { kind: 'hold', pointId: target.id });
  } else if (style === 'adaptive') {
    // thin squads (one unit) escort the nearest full squad; threatened held points get a holder; the rest take the nearest wanted point
    const full = live.filter(x => x.n >= 2);
    for (const x of live) {
      if (x.n >= 2 || full.length === 0) continue;
      let best: { squad: Squad; d: number } | null = null;
      for (const f of full) { const d = arcBetween(f.dir, x.dir, radius); if (!best || d < best.d) best = { squad: f.squad, d }; }
      if (best) { const leader = alive(state, best.squad)[0]; give(x.squad, { kind: 'escort', unitId: leader.id }); }
    }
    for (const site of threatened) { const q = nearestSquad(site.surfaceDir); if (q) give(q, { kind: 'hold', pointId: site.id }); }
    // 69.6c: a point being lost or just lost — its nearest squads not holding another, to take it back
    for (const id of [...counter].sort((a, b) => a - b)) {
      const site = sites.find(x => x.id === id);
      if (!site) continue;
      for (let i = 0; i < COUNTER_ATTACK.squads; i++) { const q = keeperOr(site); if (q) give(q, { kind: 'take', pointId: site.id }); }
    }
    // 66.13 (GameConfig.holdAndPush, ai/holdAndPush.ts): an owned point of a sector we still push keeps the fewest the threat
    // needs (counting a holder the threatened rule gave it), from the take, before the rest push
    if (holdingAndPushing(state, team)) {
      // (the cap's free squads count one the threatened rule already set to hold — it is one of the holders the cap allows;
      // counted out, a point both rules read as threatened kept half a squad fewer)
      const free = live.filter(x => x.n >= 2 && (!assigned.has(x.squad.id) || orders.get(x.squad.id)?.kind === 'hold')).length;
      const size = Math.max(1, Math.round(live.reduce((a, x) => a + x.n, 0) / Math.max(1, live.length)));
      for (const h of holdsWhilePushing(state, team, sites, free, size, tick)) {
        const have = [...orders].filter(([, o]) => o.kind === 'hold' && o.pointId === h.site.id).length;
        for (let i = have; i < h.squads; i++) { const q = nearestSquad(h.site.surfaceDir); if (q) give(q, { kind: 'hold', pointId: h.site.id }); }
      }
    }
    // 69.61: the side's story objectives on a point (core/storyObjectives.ts) — each not yet done gets the nearest squad:
    // a take, or a hold not yet held, to take it; a hold the side owns, one to hold it while its clock runs (69.61 step 4: a
    // transmit as a hold — its upload runs while the side stands there)
    for (const o of storyPoints(state, team)) {
      const site = sites.find(x => x.id === o.pointId);
      if (!site) continue;
      const take = wanted.includes(site);
      if (!take && (o.kind === 'take' || [...orders].some(([, x]) => x.kind === 'hold' && x.pointId === site.id))) continue;
      const q = keeperOr(site);
      if (q) give(q, { kind: take ? 'take' : 'hold', pointId: site.id });
    }
    // 69.91 (c): the side's one objective left on a point (a take, hold or transmit) — every squad no rule above gave an
    // order goes to it, rather than to the nearest point it wants (story-1's t1, its transmit at p7 its last, sent its spare
    // squads to take p1); an escort or a destroy left stays its play's and its strike's
    const open = (state.modeState.objectives ?? []).filter(o => o.team === team && o.doneTick === undefined);
    const last = open.length === 1 ? sites.find(x => x.id === storyPoints(state, team)[0]?.pointId) : undefined;
    if (last) for (const x of live) if (!assigned.has(x.squad.id)) give(x.squad, { kind: wanted.includes(last) ? 'take' : 'hold', pointId: last.id });
    // 69.6c (lane C's diagnostic: in 6 of 12 pushes the last sector's second objective was never approached): under an
    // Operations push, every objective of the active sector we want gets a squad of its own — the nearest — first
    if (state.modeState.points?.push) for (const site of [...wanted].sort((a, b) => a.id - b.id)) { const q = keeperOr(site); if (q) give(q, { kind: 'take', pointId: site.id }); }
    // remaining squads: nearest wanted point each (a point may get two if squads outnumber points); 73.8 (1): the take that
    // sticks — the point its base take names while still wanted, unless another is under STICKY_TAKE.switchShare of the way,
    // the ways read from its members on foot away from the base (takeDir)
    const home = state.board?.bases.find(b => b.team === team)?.dir ?? state.actionHomes?.[team] ?? null;
    for (const x of live) {
      if (assigned.has(x.squad.id)) continue;
      const at = takeDir(state, x.squad, home, radius) ?? x.dir;
      let target: CapturePoint | null = null, bestD = Infinity;
      for (const s of wanted) { const d = arcBetween(s.surfaceDir, at, radius); if (d < bestD) { bestD = d; target = s; } }
      const was = x.squad.match ? state.squads?.find(m => m.id === x.squad.id)?.base : undefined;
      const kept = was?.kind === 'take' && was.pointId !== undefined ? wanted.find(s => s.id === was.pointId) : undefined;
      if (kept && target && kept !== target && bestD >= STICKY_TAKE.switchShare * arcBetween(kept.surfaceDir, at, radius)) target = kept;
      if (target) give(x.squad, { kind: 'take', pointId: target.id });
      else { for (const s of owned) { const d = arcBetween(s.surfaceDir, x.dir, radius); if (d < bestD) { bestD = d; target = s; } } if (target) give(x.squad, { kind: 'hold', pointId: target.id }); }
    }
  }
  for (const s of squads) if (!orders.has(s.id)) orders.set(s.id, { kind: 'free' });
  pullUnordered(state, orders, live, sites);   // 69.5f (b): the left-over squads to a fight at the front (ai/frontPull.ts)
  // 69.5a: a take on a point the side has not resolved is a scout of its circle first
  if (state.config.roughStart) {
    const unresolved = new Map(knownObjectives(state, team, tick).filter(k => !k.resolved).map(k => [k.siteId, k] as const));
    for (const [id, o] of orders) {
      const k = o.kind === 'take' && o.pointId !== undefined ? unresolved.get(o.pointId) : undefined;
      if (k && scoutReady(state, live.find(x => x.squad.id === id)?.dir ?? null, k.surfaceDir)) orders.set(id, { kind: 'move', planetIndex: k.planetIndex, surfaceDir: k.surfaceDir, scout: true });   // (69.5f (a): within a sector of it)
    }
  }
  return orders;
}

// -----------------------------------------------------------------------------
// The commander: squads + a cadence + a ledger
// -----------------------------------------------------------------------------

export interface OrderRecord {
  squadId: number;
  order: SquadOrder;
  issuedTick: number;
  /** 'completed' | 'died' (every unit of the squad dead while open) | 'superseded' (a re-plan changed it) | 'open' */
  outcome: 'open' | 'completed' | 'died' | 'superseded';
  closedTick: number | null;
}

export interface OrderStats {
  issued: number;
  completed: number;
  died: number;
  superseded: number;
  /** Mean seconds from issue to completion over completed orders. */
  meanComplySeconds: number;
  /** By the orders a commander issues (a formation is a shape, not an order it issues; tactics have their own ledger). */
  byKind: Record<Exclude<SquadOrder['kind'], 'formation' | 'bound' | 'flank' | 'withdraw' | 'callIn' | 'mark' | 'beacon' | 'build' | 'move' | 'engage' | 'crew' | 'ride' | 'dismount' | 'demolish'>, { issued: number; completed: number; died: number }> & { ride?: { issued: number; completed: number; died: number }; demolish?: { issued: number; completed: number; died: number } };
  /** 66.4: the tactic tasks' ledger — present only for a commander with tactics on. */
  tactics?: TacticStats;
}

/** §0.11 step D: how a commander sets its side's efforts (the host sends the side's `efforts` input). */
export type SetEfforts = (efforts: EffortSpec[]) => void;

export interface Commander {
  team: number;
  style: CommanderStyle;
  squads: Squad[];
  /** The order for a unit right now (null = no commander). */
  orderFor(unitId: number): SquadOrder | null;
  /** Called every tick by the host: re-plans on the cadence and scores open orders. */
  step(state: GameState, tick: number, dt: number): void;
  stats(): OrderStats;
  records: OrderRecord[];
  /** 68.25: the stalemate's gun supports sent, in order. */
  gunSupports: readonly GunSupportRecord[];
  /** 69.5d: the base strikes sent and stood down, in order, with their reasons. */
  strikes: readonly StrikeRecord[];
  /** 415 step 6d: the HQ strike it would make over `st` on the base posture `plan` — read only (nothing issued, no record). */
  proposeHqStrike(st: GameState, plan: ReadonlyMap<number, SquadOrder>, tick: number): HqStrikeProposal | null;
  /** 415 step 6d: the carry it would make over `st` — read only (nothing issued or called). Outside a step, a proposal sees
   *  the claims of the commander's last round besides the state's (inside one, the round's so far: the state predates them). */
  proposeCarry(st: GameState, tick: number): CarryProposal;
  /** 69.72 step 5: its orders and plays the Match refused, in order, by planner. */
  refusals: readonly CommanderRefusal[];
  /** 69.73 step 2b (with intelRecord): the search — each cell a searcher was sent to, and each find, in order. */
  searches: readonly SearchRecord[];
  /** 415 step 6a: a sink for every order and play it gives, by planner, and whether the Match took it (null: off). */
  trace: ((e: CommanderIssue) => void) | null;
  /** 416 (with commanderAmbush): the ambushes, each from its pick to its end and why (ai/skills/ambush.ts). */
  ambushes: readonly AmbushRecord[];
  /** 416 (with commanderRally): the rallies, each from its order to its end and why (ai/skills/rally.ts). */
  rallies: readonly RallyRecord[];
}

/** 415 step 6a: one order (or play) the commander gave — the round, the planner that gave it, the squad, the order, the tier, the members it names and its claim, and whether the Match took it. */
export interface CommanderIssue { tick: number; planner: string; squadId: number; order: SquadOrder['kind'] | 'play' | 'call'; tier: 'base' | 'task' | 'play' | 'call'; units?: readonly number[]; claim?: OrderClaim; call?: CommanderCall; ok: boolean }

/**
 * 415 step 6c (ARCHITECTURE §5.52 (3)): the commander's calls, each its rule's (ai/airstrikeRule.ts, ai/dropRules.ts), in the
 * order a replan asks them — the airstrike before the base posture (68.39), the rest after it, on its plan's focus (69.3b
 * step 3): the dropship, a helicopter in its place, the pods, the hot rescue (its gunship and ship the rescue's while it
 * lasts), the gunship, the pickup, the supply drone, the spawn point. The trace names each call made (`order: 'call'`).
 */
export const COMMANDER_CALLS = ['airstrike', 'dropship', 'heli', 'pods', 'rescue', 'gunship', 'pickup', 'supplyDrop', 'spawnAt'] as const;
export type CommanderCall = typeof COMMANDER_CALLS[number];

/**
 * 415 step 6a (ARCHITECTURE §5.52 (3)): the commander's planners in the order a replan runs them, after the base posture
 * (planOrders) — each sees the claims the ones before it made (`claimedNow`). Step 6's later sub-steps make each a
 * generator of candidate tasks with one assignment over them; this is the order the assignment keeps. A new planner takes
 * its row where it runs — lane H's commander ambush (DECISIONS 416, ai/skills/ambush.ts) holds the row after `plays`, its
 * rally (ai/skills/rally.ts) the row after the ambush's.
 */
export const COMMANDER_PLANNERS = ['converge', 'plays', 'ambush', 'rally', 'escort', 'gunSupport', 'builds', 'hmg', 'convoys', 'hold', 'hqStrike', 'objStrike', 'carry', 'search', 'tactics'] as const;
export type CommanderPlanner = typeof COMMANDER_PLANNERS[number];

/**
 * 69.73 step 2b: the search's log — a searcher `sent` to a cell (its index among the planet's search cells), or what the side
 * `found` (the enemy's base, or a target of its destroy objective), with the cells of the enemy's half swept so far.
 */
export type SearchRecord = { tick: number; swept: number } & ({ kind: 'sent'; squadId: number; cell: number } | { kind: 'found'; what: 'base' | 'target' }
  /** 69.79: a searcher let go — no nearer its cell in INTEL.stallSeconds */
  | { kind: 'stalled'; squadId: number; cell: number });

/**
 * 69.5d step 3: the base strike's dials — `squads` sent (its nearest squads not holding a point, not a human's, with an HE
 * grenade among them), `walkRatio` (its walk to the enemy HQ at most this × its walk to its objective, unless its web
 * reaches within link range of the HQ), `seconds` a strike's demolish task runs before the commander renews it.
 */
export const BASE_STRIKE = { squads: 1, walkRatio: 1.5, seconds: 60 };

/**
 * 69.5d step 3: the order log's base-strike line — each strike sent or stood down, and why: the tickets (ours, theirs),
 * the enemies the team's picture puts within the support band of the HQ against the strike's living members, its walk to
 * the HQ over its walk to its objective (null: no objective), and whether its web reaches the HQ.
 */
export interface StrikeRecord {
  tick: number; squadIds: number[]; structureId: number; outcome: 'sent' | 'stood down';
  tickets: [number, number]; defenders: number; strength: number; walk: number | null; webReach: boolean; why: string;
}
/** 415 step 6d: the carry's proposal — the squads to send, the bearers their lift holds, the wave ship to call for one. */
export interface CarryProposal { sends: Array<{ sq: Squad; order: SquadOrder }>; lifted: number[]; pickup: { squadId: number; at: { planetIndex: number; surfaceDir: Vec3 } } | null }
/** 415 step 6d: what the HQ strike weighed — the record's reasons, without its tick, squads, target and outcome. */
export type StrikeLine = Omit<StrikeRecord, 'tick' | 'squadIds' | 'structureId' | 'outcome' | 'why'>;
/** 415 step 6d: the HQ strike's proposal — the squads to send at the HQ, or why the side stands its strike down. */
export type HqStrikeProposal =
  | { kind: 'standDown'; why: string; line: StrikeLine }
  | { kind: 'strike'; squads: Squad[]; structureId: number; line: StrikeLine; why: string };

/** 68.25: a gun support the commander sent — when, over which point, which squad in which APC, to what kind of ground. */
export interface GunSupportRecord { tick: number; pointId: number; squadId: number; structureId: number; kind: 'crest' | 'open'; /** the two units sent as its crew */ crew: number[] }

/**
 * R11 step 2: how a commander's decisions reach the units — `issue` is called for every changed squad order
 * (`free` included) and the host turns it into an `order` input through `Match.apply`; the bots read the state's squads.
 */
/** Issue an order; a host that applies it returns whether the Match took it (68.23: the bot commander re-issues a taken
 *  base order the state has since lost — a human's task cleared it — and only that). */
/** 69.72 step 5 (DECISIONS 304): what a commander's task claims its squad for — an objective's rank, or a play's own (`play`: the task is that play's next, as an objective asks the play for its squads). */
export type OrderClaim = { claim?: TaskClaim; play?: number; /** 416: the skill the task carries out (SquadTask.skill) */ skill?: SkillTag };
/** 69.72 step 5 (DECISIONS 304: a refusal is shown to its giver): a host hook's answer for an input the Match refused — its code. */
export type HookRefusal = { refused: string };
/** A host's answer to the commander from the Match's: true when it took the input, else the refusal's code. */
export const hookAnswer = (r: { ok: boolean; code?: string }): true | HookRefusal => (r.ok ? true : { refused: r.code ?? 'refused' });
/**
 * 69.72 step 5's follow-up (the coordinator's trace of the 'unknown' refusals): where a host sends the commander's order —
 * a task naming its units goes to them (`to: { units }`, no squad wanted); else the Match squad of the commander's squad's
 * first living member in one, else of its first member. The commander's squads are a fixed partition of its people and the
 * Match's move (68.10's fluid squads: a stray in none), so the first member alone was often in no squad (BUILDLIST 69.87 makes
 * the commander read the Match's). `{ refused: 'noSquad' }` when none is found. 69.87: a Match squad of the commander's
 * (`match`) is addressed by its id, no lookup.
 */
export function orderTo(state: Pick<GameState, 'squads' | 'players'>, squad: Pick<Squad, 'unitIds'> & Partial<Pick<Squad, 'id' | 'match'>>, units?: number[]): { units: number[] } | { squad: number } | HookRefusal {
  if (units?.length) return { units };
  if (squad.match && squad.id !== undefined) return { squad: squad.id };
  const living = squad.unitIds.filter(id => state.players.find(p => p.id === id)?.alive);
  for (const id of [...living, ...squad.unitIds]) { const q = squadOf(state, id); if (q) return { squad: q.id }; }
  return { refused: 'noSquad' };
}
export type IssueOrder = (squad: Squad, order: SquadOrder, tier: 'base' | 'task', expires?: number, units?: number[], claim?: OrderClaim) => boolean | void | HookRefusal;   // 69.2e: `units` names the members a task is for (a build's one planter)

/** 66.4: a tactic task's ledger entry. */
export interface TacticRecord { squadId: number; order: TacticOrder; issuedTick: number; expires: number; outcome: 'open' | 'completed' | 'expired' | 'died' | 'superseded'; closedTick: number | null }
export interface TacticStats { bound: { issued: number; completed: number; expired: number; died: number }; flank: { issued: number; completed: number; expired: number; died: number } }
/** 69.5a: a commander's scout task runs this long before it expires unless renewed (seconds). */
export const SCOUT_TASK_SECONDS = 60;
/**
 * 69.5a: a circle's sweep — where the commander's scouts look, in turn, until the point resolves: the circle's centre, then
 * `ring` of its radius out from it in `bearings` directions (a scout that saw its place without the point resolving is sent
 * to the next).
 */
export const SCOUT_SWEEP = { ring: 0.5, bearings: 6 };

/** 69.5a: the `i`th place of a circle's sweep (centre `centre` on a planet of radius `R`, the circle `radius` world units). */
export function sweepPlace(centre: Vec3, radius: number, R: number, i: number): Vec3 {
  const n = vecNormalize(centre);
  const k = i % (SCOUT_SWEEP.bearings + 1);
  if (k === 0) return n;
  const { tx, ty } = surfaceTangentFrame(n);
  const a = (2 * Math.PI * (k - 1)) / SCOUT_SWEEP.bearings, arc = (SCOUT_SWEEP.ring * radius) / R;
  const way = vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a)));
  return vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(way, dsin(arc))));
}

/** A tactic task runs this long before it expires (seconds). */
export const TACTIC_SECONDS = 40;

/**
 * 68.4: a commander's reserve call (the same `reserves` input the table sends). With one given, the commander holds its
 * fallen back until a squad's worth waits (or all that is left of the reserve), then calls them in together — a squad
 * arriving, not a trickle — and never holds longer than two waves.
 */
export type CallReserves = (call: 'hold' | 'call') => void;
/** 68.39: the side's airstrike call at a point (the seat's `airstrike` input). */
export type CallAirstrike = (at: { planetIndex: number; surfaceDir: Vec3 }) => void;
/** 69.3b step 3: the side's drop calls (the seat's `dropship` / `podDrop` inputs, with the drop squad's order) and its order on where its waves land (`spawnAt`). */
export interface DropCalls {
  dropship?: (at: { planetIndex: number; surfaceDir: Vec3 }, order: SquadOrder, hot?: boolean) => void;   // (69.52 step 4: `hot`, the call pressed straight in)
  pods?: (at: { planetIndex: number; surfaceDir: Vec3 }, order: SquadOrder) => void;
  /** 69.59 step 1: one of the side's helicopters in a dropship's place (its call, with the drop squad's order) */
  heli?: (at: { planetIndex: number; surfaceDir: Vec3 }, order: SquadOrder) => void;
  /** 63.13: the side's gunship over a point */
  gunship?: (at: { planetIndex: number; surfaceDir: Vec3 }) => void;
  spawnAt?: (at: 'forward' | 'base') => void;
  /** 69.18: the side's wave ship sent to fetch a squad home. */
  pickup?: (squadId: number, at: { planetIndex: number; surfaceDir: Vec3 }, priority?: 'redeploy', craft?: 'heli') => void;   // (272 (2): `redeploy`, the low priority; 69.59 step 2: `craft`, a helicopter's)
  /** 69.62: the side's hot rescue of a squad at `at`, its gunship over `threat`. */
  rescue?: (squadId: number, at: { planetIndex: number; surfaceDir: Vec3 }, threat: { planetIndex: number; surfaceDir: Vec3 }, cause?: 'crew' | 'cutOff') => void;
  /** 69.32 step 2: a supply drone called onto ground the side holds or contests. */
  supplyDrop?: (at: { planetIndex: number; surfaceDir: Vec3 }, load: 'smallArms' | 'rocket' | 'mortarBomb') => void;
}
const RESERVE_HOLD_WAVES = 2;

/**
 * The ride a squad is carrying out, if one is in force: its order now (its first living member's, through the state) is a
 * ride whose transport still stands and which has not yet arrived (`rideArrived`). Null otherwise — no ride, a lapsed one,
 * a destroyed transport, or the ride done.
 */
export function rideInForce(st: GameState, squad: Squad, tick: number): Extract<SquadOrder, { kind: 'ride' }> | null {
  const first = alive(st, squad)[0];
  if (!first) return null;
  const order = squadOrderFor(st, first.id, tick);
  if (!order || order.kind !== 'ride') return null;
  const transport = st.structures.find(x => x.id === order.structureId);
  if (!transport || transport.health <= 0) return null;
  return rideArrived(st, order) ? null : order;
}

/** 68.23: the order in force for a squad when a human gave it (its first living member's, through the state), else null. */
export function humanOrderFor(st: GameState, squad: Squad, tick: number): SquadOrder | null {
  const first = alive(st, squad)[0];
  if (!first || squadOrderSourceFor(st, first.id, tick) !== 'human') return null;
  return squadOrderFor(st, first.id, tick);
}

/** 68.22 step 4: form a play — the host applies one `coordinate` input over these squads at the point (by the commander); true when the Match took it.
 *  66.15: or at a place, with the support named and `here` (the standoff manoeuvre). */
export type FormPlay = (squads: Squad[], objective: number | { planetIndex: number; surfaceDir: Vec3 }, opts?: { support?: Squad[]; here?: true; flank?: true; mounted?: true }) => boolean | HookRefusal;
/**
 * 69.72 step 5 (DECISIONS 304): one of the commander's orders or plays the Match refused — the planner that gave it (the
 * step's section: posture, scout, standoff, converge, plays, escort, gunSupport, builds, hmg, convoys, hqStrike, objStrike,
 * carry, tactics…), the squad, the order's kind and tier (a play's: 'play'), and the code ('unknown' from a hook that says
 * only false).
 */
export interface CommanderRefusal { tick: number; planner: string; squadId: number; order: SquadOrder['kind'] | 'play'; tier: 'base' | 'task' | 'play'; code: string }

export function createCommander(state: GameState, team: number, style: CommanderStyle, squadSize = 3, replanSeconds = REPLAN_SECONDS, issue?: IssueOrder, tactics = false, callReserves?: CallReserves, formPlay?: FormPlay, callAirstrike?: CallAirstrike, drops?: DropCalls, setEfforts?: SetEfforts): Commander {
  // 69.87 (GameConfig.commanderSquads): the Match's squads, read again each step; off (or a state without squads), its own
  // fixed partition by unit id
  const own = commanderSquadsOn(state.config) && matchSquadsOf(state, team).length > 0;
  let squads = own ? matchSquadsOf(state, team) : squadsOf(state, team, squadSize);
  const standoff = createStandoffPlanner(team);   // 66.15
  const ambush = createAmbushPlanner(team);   // 416
  const rally = createRallyPlanner(team);   // 416: the rally
  const byUnit = new Map<number, number>(), byId = new Map<number, Squad>();
  const index = () => { byUnit.clear(); byId.clear(); for (const s of squads) { byId.set(s.id, s); for (const id of s.unitIds) byUnit.set(id, s.id); } };
  index();
  let current = new Map<number, SquadOrder>();
  const landed = new Set<number>();   // 68.23: squads whose last base order the Match took
  const scoutUntil = new Map<number, number>();   // 69.5a: each scouting squad's task, the tick it runs out
  const scoutSite = new Map<number, number>();   // 69.5a: the point each scouting squad looks for
  const sweepAt = new Map<number, number>();   // 69.5a: each unresolved point's place in its sweep
  const open = new Map<number, OrderRecord>();
  const records: OrderRecord[] = [];
  const tacticRecords: TacticRecord[] = [];
  const openTactic = new Map<number, TacticRecord>();
  let nextPlanTick = 0;
  // (GameConfig.commanderPhase: after the first plan, this side's beat a share of the period after team 0's — the sides'
  // re-plans and the strikes they call were all on one tick, the Story's a median 7 ms and the worst 43)
  const phaseFlag = state.config.commanderPhase;
  const phased = Array.isArray(phaseFlag) ? phaseFlag[team] !== false : phaseFlag !== false;
  const teamCount = 1 + Math.max(team, ...state.players.map(p => p.teamIndex));
  let phaseLeft = phased;
  const held = new Set<number>();   // 229 (a): the points this side has held (as it knows them), for the efforts' re-take
  let spawnBase = false;   // 69.3b step 3: its waves sent to its base while it masses (its own order, to lift)
  const strandedSince = new Map<number, number>();   // 69.18: since when each of its squads has stood stranded (the pickup's wait)
  const cutOffSince = new Map<number, number>();   // 69.62: since when each has stood cut off (the rescue's wait)
  const sameOrder = (a: SquadOrder | undefined, b: SquadOrder) => !!a && JSON.stringify(a) === JSON.stringify(b);
  // 69.72 step 5 (DECISIONS 304): one claim a squad, the Match's. A planner picks a squad nothing claims above the
  // commander's routine (`free`), or one already on its own job; what this step's own orders claimed is in `claimedNow`
  // (the step reads the state as it was when it began). `claimOf` with `members`: a member's errand too (a planter's build).
  const claimedNow = new Map<number, number>();
  const claimOf = (st: GameState, sq: Squad, tick: number, members = true): number => {
    const id = squadOf(st, sq.unitIds[0])?.id;
    return Math.min(id === undefined ? Infinity : squadClaimRank(st, id, tick, members), claimedNow.get(sq.id) ?? Infinity);
  };
  const free = (st: GameState, sq: Squad, tick: number): boolean => claimOf(st, sq, tick) >= TASK_RANK.routine;
  /** The play a squad's task in force carries out, if any. */
  const playOf = (st: GameState, sq: Squad, tick: number): number | undefined => squadOf(st, sq.unitIds[0])?.tasks.find(x => x.play !== undefined && !x.units && (x.expires === undefined || tick < x.expires))?.play;
  const mountedNow = new Set<number>();   // (this step's mounted plays' squads: the step's state predates them)
  const mountedPlay = (st: GameState, sq: Squad, tick: number): boolean => { const id = playOf(st, sq, tick); return mountedNow.has(sq.id) || (id !== undefined && (st.plays ?? []).some(p => p.id === id && !!p.mounted && p.phase !== 'done')); };
  /**
   * A squad a named errand (a gun's crew, a build, an HMG, a supply run: 304's rank 3) may take a member from: on the routine,
   * or in a play on foot — never a mounted play's (the column: lane E's 69.61 step 5 finding 4), an objective's, a lift's or
   * a human's (the errand outranks a play; the commander keeps its columns and objectives whole).
   */
  const errandFree = (st: GameState, sq: Squad, tick: number): boolean => { const c = claimOf(st, sq, tick, false); return c >= TASK_RANK.routine || (c === TASK_RANK.play && !mountedPlay(st, sq, tick)); };
  /** An objective's task for a squad a play holds is the play's own next (`play`: 304, the objective asks the play), else its claim. */
  const claimFor = (st: GameState, sq: Squad, tick: number, claim: TaskClaim): OrderClaim => {
    const t = squadOf(st, sq.unitIds[0])?.tasks.find(x => x.play !== undefined && !x.units && (x.expires === undefined || tick < x.expires));
    return t ? { play: t.play } : { claim };
  };
  // (69.72 step 5: what the Match refused, by the planner giving it — `planner` and `stepTick` set as the step goes)
  const refusals: CommanderRefusal[] = [];
  const cmd: { trace: ((e: CommanderIssue) => void) | null } = { trace: null };   // (415 step 6a: the returned commander's `trace`)
  let planner = 'posture', stepTick = 0;
  const refusedCode = (r: boolean | void | HookRefusal): string | null => (typeof r === 'object' ? r.refused : r === false ? 'unknown' : null);
  if (issue) {
    const give = issue;
    issue = (sq, order, tier, expires, units, claim) => {
      const r = give(sq, order, tier, expires, units, claim), code = refusedCode(r);
      if (cmd.trace) cmd.trace({ tick: stepTick, planner, squadId: sq.id, order: order.kind, tier, ...(units ? { units: [...units] } : {}), ...(claim ? { claim } : {}), ok: code === null });
      if (code !== null) { refusals.push({ tick: stepTick, planner, squadId: sq.id, order: order.kind, tier, code }); return false; }
      if (tier === 'task' && order.kind !== 'mark') claimedNow.set(sq.id, Math.min(claimedNow.get(sq.id) ?? Infinity, taskRank({ order, units, by: 'commander', ...claim })));
      return r;
    };
  }
  if (formPlay) {
    const form = formPlay;
    formPlay = (sqs, objective, opts) => {
      const code = refusedCode(form(sqs, objective, opts)), ok = code === null;
      if (cmd.trace) cmd.trace({ tick: stepTick, planner, squadId: sqs[0]?.id ?? -1, order: 'play', tier: 'play', ok });
      if (!ok) refusals.push({ tick: stepTick, planner, squadId: sqs[0]?.id ?? -1, order: 'play', tier: 'play', code });
      if (ok) for (const sq of [...sqs, ...(opts?.support ?? [])]) { claimedNow.set(sq.id, Math.min(claimedNow.get(sq.id) ?? Infinity, TASK_RANK.play)); if (opts?.mounted) mountedNow.add(sq.id); }   // (a play's, rank 4)
      return ok;
    };
  }

  const complete = (st: GameState, squad: Squad, order: SquadOrder): boolean => {
    const sites = st.modeState.points?.sites ?? [];
    const us = alive(st, squad);
    if (us.length === 0) return false;
    if (order.kind === 'engage' || order.kind === 'crew' || order.kind === 'move' || order.kind === 'dismount') return false;   // 68.0: no brain obeys these yet; their completion is lane C's
    if (order.kind === 'free' || order.kind === 'formation' || order.kind === 'withdraw' || order.kind === 'fireMission' || order.kind === 'callIn' || order.kind === 'mark' || order.kind === 'beacon' || order.kind === 'build' || isTactic(order)) return false;   // §15.2: a fire mission is the guns', done when they fire   // a formation is a shape, never completed; tactics have their own rule
    // 63.1d step 4: a ride is done when its transport stood at the place and a member is out on foot there
    if (order.kind === 'ride') {
      const place = orderPlace(st, order);
      if (!place || !rideArrived(st, order)) return false;
      const r = st.planets[place.planetIndex].radius, near = RIDE_ARRIVE * PERSON_BODY_HEIGHT * personSetOf(st).k;
      return us.some(u => !u.mount && u.planetIndex === place.planetIndex && arcBetween(u.surfaceDir, place.surfaceDir, r) <= near);
    }
    if (order.kind === 'demolish') { const s = st.structures.find(x => x.id === order.structureId); return !s || s.health <= 0; }   // 69.5d: done when it falls
    if (order.kind === 'escort') {
      const charge = st.players.find(p => p.id === order.unitId);
      if (!charge || !charge.alive) return false;
      const r = st.planets[charge.planetIndex].radius;
      return us.some(u => arcBetween(u.surfaceDir, charge.surfaceDir, r) < commanderArcs(st).escort);
    }
    const site = sites.find(s => s.id === order.pointId);
    if (!site) return false;
    const r = st.planets[site.planetIndex].radius;
    const arrived = us.some(u => u.planetIndex === site.planetIndex && arcBetween(u.surfaceDir, site.surfaceDir, r) < commanderArcs(st).arrive);
    if (order.kind === 'take') return site.owner === team && site.capturer === null && arrived;
    return arrived;   // hold: standing on it
  };

  // 69.6c: the points under counter-attack (point id → the tick it was found being lost), and who held what last tick
  const counter = new Map<number, number>();
  const heldBefore = new Map<number, boolean>();
  const watchLosses = (st: GameState, tick: number, dt: number): boolean => {
    let lost = false;
    const live = activeSites(st);
    for (const site of live) {
      const ours = site.owner === team;
      const losing = (ours && site.capturer !== null && site.capturer !== team) || (!ours && heldBefore.get(site.id) === true);
      heldBefore.set(site.id, ours);
      if (losing && !counter.has(site.id)) { counter.set(site.id, tick); lost = true; }
    }
    for (const [id, since] of counter) {
      const site = live.find(x => x.id === id);
      if (!site || (tick - since) * dt > COUNTER_ATTACK.seconds || (site.owner === team && (site.capturer === null || site.capturer === team))) counter.delete(id);
    }
    return lost;
  };

  /**
   * 69.87: the team's Match squads as they stand now (68.10's fluid squads re-form them, a drop brings one in) — a squad keeps
   * its id across re-forms, so the per-squad entries stay with it; a squad gone from the list closes its open order and tactic
   * and drops its entries.
   */
  const dropOrder = (st: GameState, id: number): SquadOrder | null => {
    const q = st.squads?.find(x => x.id === id), o = q?.base;
    if (!q?.drop || q.baseBy !== 'commander' || o?.kind !== 'take' || o.pointId === undefined) return null;
    const site = st.modeState.points?.sites.find(x => x.id === o.pointId);
    return site && site.owner !== team ? o : null;
  };
  let seen = state.squads;   // (the state's squads as last read: a new array whenever any squad changes)
  const resync = (st: GameState, tick: number) => {
    if (st.squads === seen) return;
    seen = st.squads;
    const now = matchSquadsOf(st, team);
    if (now.length === squads.length && now.every((s, i) => s.id === squads[i].id && s.unitIds.length === squads[i].unitIds.length && s.unitIds.every((u, j) => u === squads[i].unitIds[j]))) return;
    squads = now;
    index();
    const gone = (id: number) => !byId.has(id);
    for (const [id, rec] of open) if (gone(id)) { rec.outcome = 'superseded'; rec.closedTick = tick; open.delete(id); }
    for (const [id, rec] of openTactic) if (gone(id)) { rec.outcome = 'superseded'; rec.closedTick = tick; openTactic.delete(id); }
    for (const m of [current, scoutUntil, scoutSite, resting]) for (const id of [...m.keys()]) if (gone(id)) m.delete(id);
    for (const id of [...landed]) if (gone(id)) landed.delete(id);
    carrying = carrying.filter(id => !gone(id));
  };

  // 415 step 6b: the side's read for this step (ai/sideRead.ts), made at its start and shared by the planners
  let side: SideRead = sideRead(state, team, 0);
  const step = (st: GameState, tick: number, dt: number) => {
    side = sideRead(st, team, tick);
    claimedNow.clear();
    // (415 step 6c: a call made, for the trace)
    const called = (call: CommanderCall, squadId = -1) => { if (cmd.trace) cmd.trace({ tick, planner: 'calls', squadId, order: 'call', call, tier: 'call', ok: true }); };
    mountedNow.clear();
    stepTick = tick;
    if (own) resync(st, tick);
    // score open orders
    for (const [squadId, rec] of open) {
      const squad = byId.get(squadId)!;
      if (alive(st, squad).length === 0) { rec.outcome = 'died'; rec.closedTick = tick; open.delete(squadId); continue; }
      if (complete(st, squad, rec.order)) { rec.outcome = 'completed'; rec.closedTick = tick; open.delete(squadId); }
    }
    if (style === 'none') return;
    // 66.15 (GameConfig.standoffManoeuvre, ai/standoffManoeuvre.ts): the standoff read and its manoeuvre, once a second —
    // through the plays' input (a play at the enemy group, the base named as the support)
    planner = 'standoff';
    if (issue && formPlay && standoffOn(st, team) && tick % Math.max(1, Math.round(1 / dt)) === 0) standoff.step(st, squads, tick, issue, formPlay, sq => humanOrderFor(st, sq, tick) !== null);
    // 416 (GameConfig.commanderAmbush, ai/skills/ambush.ts): the squads lying in wait, once a second — their phases, their tasks renewed
    planner = 'ambush';
    if (issue && ambushOn(st, team)) ambush.step(st, squads, tick, sq => free(st, sq, tick), issue);
    // 416 (GameConfig.commanderRally, ai/skills/rally.ts): a squad cut to half in a fight falls back to a hidden rally point, once a second
    planner = 'rally';
    if (issue && rallyOn(st, team)) rally.step(st, squads, tick, sq => free(st, sq, tick), issue);
    // 69.6c: under an Operations push, a point being lost re-plans now (elsewhere a skirmish's points change hands all match);
    // 73.8c: and in a match with story objectives, whose points change hands a few times a match (lane H's trace: 6–12 losses on
    // six Story seeds, the old owner's nearest unit 200–1,100 people off and almost never sent back)
    if (style === 'adaptive' && (st.modeState.points?.push || st.modeState.objectives?.length) && watchLosses(st, tick, dt)) nextPlanTick = tick;
    if (tick < nextPlanTick) return;
    const period = Math.max(1, Math.round(replanSeconds / dt));
    nextPlanTick = tick + period + (phaseLeft ? Math.round((team * period) / teamCount) : 0);
    phaseLeft = false;
    if (callReserves) planReserves(st, tick, dt);
    // 68.39: the side's airstrike, on its knowledge and its pacing (ai/airstrikeRule.ts) — one a re-plan at most
    if (callAirstrike && st.airstrikes) { const at = airstrikeTarget(st, team, tick); if (at) { for (let k = 0; k < (at.salvo ?? 1); k++) callAirstrike({ planetIndex: at.planetIndex, surfaceDir: at.surfaceDir }); called('airstrike'); } }   // (69.9 step 5: two back to back to saturate a known mount)
    // §0.11 step D (GameConfig.efforts, ai/efforts.ts): the adaptive commander runs its side as efforts — the squads no human
    // orders (theirs are theirs, 68.23), the plan's efforts set through the side's `efforts` input
    let next: Map<number, SquadOrder>;
    if (style === 'adaptive' && effortsOn(st.config, team)) {
      const sites = sitesAsKnown(st, team, activeSites(st), tick);
      for (const s of sites) if (s.owner === team) held.add(s.id);
      const lost = new Set(sites.filter(s => held.has(s.id) && s.owner !== team).map(s => s.id));
      // (69.61 step 5: never the escort's squads — a raid on a relay took the column's assault off its ride, and the play ended)
      const plan = planEfforts(st, team, squads.filter(sq => free(st, sq, tick)), tick, lost);   // (69.72 step 5: a squad no human, lift, play or objective holds)
      next = plan.orders;
      for (const sq of squads) if (!next.has(sq.id)) next.set(sq.id, { kind: 'free' });
      setEfforts?.(plan.efforts);
    } else next = planOrders(st, team, squads, style, [...counter.keys()], tick, side);
    // 69.3b step 3 (ai/dropRules.ts): its drops on its knowledge and its pacing — the dropship short of its focus, the pods deep
    // (an undefended rear objective, else behind its focus) — and its waves held at its base while it masses for a play
    if (drops) {
      const focus = focusPoint(st, team, next);
      if (drops.dropship && st.dropships) { const c = dropshipCall(st, team, tick, focus); if (c) { drops.dropship(c.at, c.order, !!c.hot); called('dropship'); } }
      // 69.59 step 1: out of dropships, a helicopter brings them in instead (paced on its own count)
      if (drops.heli && countOf(st.config, 'helicopters') && dropshipsLeft(st, team) <= 0) { const c = dropshipCall(st, team, tick, focus, true); if (c) { drops.heli(c.at, c.order); called('heli'); } }
      if (drops.pods && st.pods) { const c = podCall(st, team, tick, focus); if (c) { drops.pods(c.at, c.order); called('pods'); } }
      // 69.62 (§5.31): a squad cut off in contact, or a crashed crew, rescued — the gunship over the threat, the lift in under it (step 3: a helicopter while the side has one, else the wave ship);
      // its gunship and its ship are the rescue's while it lasts (never a human's squad)
      let rescuing = !!(drops.rescue && rescueLive(st, team));
      if (drops.rescue && st.config.hotRescue && defaultOn(st.config, 'shipPickup') && !rescuing) {
        const own = squads.filter(sq => humanOrderFor(st, sq, tick) === null).map(sq => squadOf(st, alive(st, sq)[0]?.id ?? -1)).filter((x): x is NonNullable<typeof x> => !!x);
        const now = new Set(cutOffSquads(st, team, tick, own).map(x => x.id));
        for (const id of [...cutOffSince.keys()]) if (!now.has(id)) cutOffSince.delete(id);
        for (const id of now) if (!cutOffSince.has(id)) cutOffSince.set(id, tick);
        const c = rescueCall(st, team, tick, own, cutOffSince);
        if (c) { drops.rescue(c.squadId, c.at, c.threat, c.cause); rescuing = true; called('rescue', c.squadId); }
      }
      if (drops.gunship && countOf(st.config, 'gunships') && !rescuing) { const c = gunshipCall(st, team, tick, focus); if (c) { drops.gunship(c.at); called('gunship'); } }   // 63.13
      // 69.18: a squad stranded far from every objective and its base fetched home by the wave ship, for retasking (never a human's)
      if (drops.pickup && defaultOn(st.config, 'shipPickup') && !rescuing) {
        const own = squads.filter(sq => humanOrderFor(st, sq, tick) === null).map(sq => squadOf(st, alive(st, sq)[0]?.id ?? -1)).filter((x): x is NonNullable<typeof x> => !!x);
        const now = new Set(strandedSquads(st, team, tick, own));
        for (const id of [...strandedSince.keys()]) if (!now.has(id)) strandedSince.delete(id);
        for (const id of now) if (!strandedSince.has(id)) strandedSince.set(id, tick);
        const c = pickupCall(st, team, tick, own, strandedSince);
        if (c) { drops.pickup(c.squadId, c.at, c.priority, c.craft); called('pickup', c.squadId); }
      }
      // 69.32 step 2: a squad running low with no crate within the walk gets a supply drone where it stands (never a human's)
      if (drops.supplyDrop && st.supplyDrones) {
        const own = squads.filter(sq => humanOrderFor(st, sq, tick) === null).map(sq => squadOf(st, alive(st, sq)[0]?.id ?? -1)).filter((x): x is NonNullable<typeof x> => !!x);
        const at = supplyDropCall(st, team, tick, own);
        if (at) { drops.supplyDrop(at, 'smallArms'); called('supplyDrop'); }
      }
      if (drops.spawnAt) {
        const mass = massing(st, team) || crewWanted(st, team);   // (69.14: and while a vehicle at its base wants a crew)
        if (mass && !spawnBase && st.spawnAt?.[team] === undefined) { drops.spawnAt('base'); spawnBase = true; called('spawnAt'); }   // (never over a human's order)
        else if (!mass && spawnBase) { if (st.spawnAt?.[team] === 'base') { drops.spawnAt('forward'); called('spawnAt'); } spawnBase = false; }
      }
    }
    // 69.5a: the scouts sweep their circle — a squad whose scout task has gone (it saw its place) with the point still
    // unresolved moves that point's sweep on; each scout is sent to its point's place in the sweep
    if (st.config.roughStart) {
      const circles = knownObjectives(st, team, tick).filter(k => !k.resolved);
      const live = (sq: Squad) => alive(st, sq).some(u => squadTaskFor(st, u.id, tick)?.order.kind === 'move');
      for (const [sqId, siteId] of scoutSite) {
        const sq = byId.get(sqId);
        if (sq && !live(sq) && circles.some(k => k.siteId === siteId)) { sweepAt.set(siteId, (sweepAt.get(siteId) ?? 0) + 1); scoutUntil.delete(sqId); }
      }
      scoutSite.clear();
      for (const [sqId, o] of next) {
        if (o.kind !== 'move' || !o.scout) continue;
        const k = circles.find(c => c.surfaceDir.x === o.surfaceDir!.x && c.surfaceDir.y === o.surfaceDir!.y && c.surfaceDir.z === o.surfaceDir!.z);
        if (!k) continue;
        scoutSite.set(sqId, k.siteId);
        next.set(sqId, { kind: 'move', planetIndex: k.planetIndex, surfaceDir: sweepPlace(k.surfaceDir, roughRadius(st), st.planets[k.planetIndex].radius, sweepAt.get(k.siteId) ?? 0), scout: true });
      }
    }
    for (const squad of squads) {
      const o = next.get(squad.id) ?? { kind: 'free' as const };
      planner = o.kind === 'move' && o.scout ? 'scout' : 'posture';
      // a ride in force (ordered, boarding or en route) is not re-tasked until it arrives, its transport is gone or the
      // order lapses — only a withdraw overrides it (lane B's ride trace: 2 of 12 rides replaced before anyone boarded)
      const ride = o.kind === 'withdraw' ? null : rideInForce(st, squad, tick);
      if (ride) { next.set(squad.id, ride); continue; }
      // 68.23 (decision 153, the owner: "I like the 'only order people with no orders' idea"): a squad with a human's
      // order in force is the human's — the bot commander neither re-tasks it nor re-issues over it; it plans the squad
      // again once that order is gone (its record becomes the human's, so the first plan after differs and is issued)
      const human = humanOrderFor(st, squad, tick);
      if (human) { next.set(squad.id, human); continue; }
      // 69.87: a drop squad the commander called keeps its drop's take till the point is the side's (it was flown there for
      // it — the re-plan's nearest point sent story-1's first drop elsewhere); then it is planned as any squad
      const dropped = squad.drop ? dropOrder(st, squad.id) : null;
      if (dropped) { next.set(squad.id, dropped); continue; }
      // 66.15: a squad in the standoff manoeuvre under way (its manoeuvre squad, its base) is the manoeuvre's until it ends
      if (standoff.holds(squad.id)) { const was = current.get(squad.id); if (was) next.set(squad.id, was); continue; }
      // 416: a guard lying in wait on its point's way (ai/skills/ambush.ts) is still that point's guard: it keeps its hold till the ambush ends
      if (ambush.holds(squad.id)) { const was = current.get(squad.id); if (was) next.set(squad.id, was); continue; }
      // 416: a squad rallying (ai/skills/rally.ts) keeps its own order — the commander has it again when the rally ends
      if (rally.holds(squad.id)) { const was = current.get(squad.id); if (was) next.set(squad.id, was); continue; }
      // 69.72 step 5 (DECISIONS 304; 69.61 step 5's escort guard, every claim now): a squad held for a lift, a play or an
      // objective keeps its posture till the claim ends (its errand-running members don't hold the squad's posture)
      if (claimOf(st, squad, tick, false) < 6) { const was = current.get(squad.id); if (was) next.set(squad.id, was); continue; }
      // its own last plan — unless the Match took it and the squad's base has lost it since (68.23: a human's task
      // cleared it), when it is issued again
      const stateSq = landed.has(squad.id) ? squadOf(st, squad.unitIds[0]) : null;
      const lost = !!stateSq && !sameOrder(stateSq.base ?? { kind: 'free' as const }, o);
      // 69.5a: a scout is a task (a move is never a posture): issued, and renewed a re-plan before it would run out, while
      // the plan still wants it; the take once the circle resolves is a base order again (which clears the task)
      if (o.kind === 'move' && o.scout) {
        const until = scoutUntil.get(squad.id), same = sameOrder(current.get(squad.id), o);
        if (same && until !== undefined && tick + Math.max(1, Math.round(replanSeconds / dt)) < until) continue;
        const end = tick + Math.max(1, Math.round(SCOUT_TASK_SECONDS / dt));
        issue?.(squad, o, 'task', end);
        scoutUntil.set(squad.id, end);
        landed.delete(squad.id);
        if (!same) {
          const prev = open.get(squad.id);
          if (prev) { prev.outcome = 'superseded'; prev.closedTick = tick; open.delete(squad.id); }
          const rec: OrderRecord = { squadId: squad.id, order: o, issuedTick: tick, outcome: 'open', closedTick: null }; open.set(squad.id, rec); records.push(rec);
        }
        continue;
      }
      scoutUntil.delete(squad.id);
      if (sameOrder(current.get(squad.id), o) && !lost) continue;
      if (issue?.(squad, o, 'base') === true) landed.add(squad.id); else landed.delete(squad.id);
      const prev = open.get(squad.id);
      if (prev) { prev.outcome = 'superseded'; prev.closedTick = tick; open.delete(squad.id); }
      if (o.kind !== 'free') { const rec: OrderRecord = { squadId: squad.id, order: o, issuedTick: tick, outcome: 'open', closedTick: null }; open.set(squad.id, rec); records.push(rec); }
    }
    current = next;
    // 415 step 6a: the planners in COMMANDER_PLANNERS' order, each where it is on (`planner` names it for the refusals and the trace)
    let staging: ReturnType<typeof planConverge> = undefined as unknown as ReturnType<typeof planConverge>;
    const stages: Record<CommanderPlanner, { on: () => boolean; run: () => void }> = {
      converge: { on: () => true, run: () => { staging = planConverge(st, next, tick, dt); searchedThisRound = false; } },
      plays: { on: () => !!formPlay, run: () => planPlays(st, next, tick, staging) },
      // 416: a guard lies in wait on the enemy's way to its point (ai/skills/ambush.ts)
      ambush: { on: () => !!issue && ambushOn(st, team), run: () => ambush.plan(st, squads, next, tick, sq => free(st, sq, tick), issue!) },
      // 416: a squad cut to half in a fight falls back to a rally point (ai/skills/rally.ts; its step picks each second too)
      rally: { on: () => !!issue && rallyOn(st, team), run: () => rally.plan(st, squads, tick, sq => free(st, sq, tick), issue!) },
      // 69.61 step 5: before the errands below (69.72 step 5: the play just formed claims its squads at once: `claimedNow`)
      escort: { on: () => !!formPlay && !!st.config.storyEscort && !!st.config.mountedAssault && !!st.modeState.objectives, run: () => planEscort(st, tick) },
      gunSupport: { on: () => !!formPlay && !!issue, run: () => planGunSupport(st, next, tick, dt) },
      builds: { on: () => !!st.config.relays, run: () => planBuilds(st, next, tick) },
      // 69.4 step 6's bot half (b): an HMG at a point it holds (core/hmgErrand.ts)
      hmg: { on: () => !!issue && !!st.config.buildKits, run: () => { const h = planHmg(st, team, [...next.values()].flatMap(o => ((o.kind === 'take' || o.kind === 'hold') && o.pointId !== undefined ? [o.pointId] : [])), tick); if (h && freeUnit(st, h.unitId, tick)) issue!(squads.find(sq => sq.unitIds.includes(h.unitId)) ?? { id: -1, team, unitIds: [h.unitId] }, h.order, 'task', undefined, [h.unitId]); } },
      // 69.4 step 5 (DECISIONS 166): the supply runs — each a task for one unit on top of its squad's order (core/convoys.ts)
      convoys: { on: () => !!issue && convoysOn(st.config), run: () => {
        for (const p of planConvoys(st, team, tick)) {
          if (!freeUnit(st, p.unitId, tick)) continue;   // (69.61 step 5: not from the escort's squads; 69.72 step 5: nor any claimed squad's)
          const squad = squads.find(sq => sq.unitIds.includes(p.unitId)) ?? { id: -1, team, unitIds: [p.unitId] };
          issue!(squad, p.order, 'task', p.expires, [p.unitId]);
        }
      } },
      // 69.79 step 4 (before the strikes, the carry and the search: its squad is claimed first)
      hold: { on: () => !!st.modeState.objectives, run: () => planHold(st, next, tick, dt) },
      hqStrike: { on: () => true, run: () => planStrike(st, next, tick, dt) },
      objStrike: { on: () => !!st.modeState.objectives, run: () => planObjectiveStrike(st, next, tick, dt) },   // 69.50 step 1
      carry: { on: () => !!st.config.storyCarry && !!st.modeState.objectives, run: () => planCarry(st, next, tick, dt) },   // 69.61 step 4
      // 69.73 step 2b: the base (the objective strike searches for its targets)
      search: { on: () => !!st.config.intelRecord && !searchedThisRound, run: () => planSearch(st, next, tick, dt) },
      tactics: { on: () => tactics, run: () => planTactics(st, tick) },
    };
    for (const name of COMMANDER_PLANNERS) { planner = name; const s = stages[name]; if (s.on()) s.run(); }
  };

  /**
   * 69.50 step 1 (DIRECTION §0.13): a *destroy a set* objective of the side's — the nearest fit squad (HE grenades, not on a
   * hold, no human's order on it, not already striking the HQ) sent on a demolish task at the set's nearest standing target,
   * re-planned (STORY.strikeSquads squads, a task of STORY.strikeSeconds, renewed); stood down when the set is gone. A *hold*
   * objective rides on the capture point the commander already takes and holds.
   */
  /**
   * 69.61: a unit seated at a vehicle's wheel or gun — a crew, not a striker: seated, it never walks to the target, and a parked
   * technical's crew stood 3,500 people off its generator for 20 minutes (lab-7). A passenger in a bay rides with its squad.
   * (Lane C's measure: and a fire-support vehicle's gun seat whatever its role — the mortar carrier's tube is a passenger's
   * hatch, core/baseCrew.ts crewSeats; its gunner, sent on a strike, stayed aboard at 260 s and 600 s.)
   */
  const crewing = (st: GameState, u: { mount?: { bodyId: number; seat: number } }): boolean => {
    if (!u.mount) return false;
    const v = st.structures.find(x => x.id === u.mount!.bodyId), role = v?.seats?.[u.mount.seat]?.role;
    return role === 'driver' || role === 'weapon' || (!!v && crewSeats(v)?.gun === u.mount.seat);
  };
  /** A unit whose squad nothing claims above the routine (69.72 step 5: the escort's guard for the HMG and the supply runs, every claim now). */
  const freeUnit = (st: GameState, unitId: number, tick: number): boolean => { const sq = squads.find(x => x.unitIds.includes(unitId)); return !sq || errandFree(st, sq, tick); };
  /**
   * DECISIONS 298 (c) (the owner: "Restock and return"): a squad able to strike the destroy objective — an HE grenade among
   * it; or, already on the strike in the story, everyone out with the side's stock still holding some, so it restocks at a
   * crate or FOB and goes back (actionBot's walk, apcLoop's ride) instead of dropping out for a squad that starts the walk
   * over. The HQ strike's squads are not kept so (the coordinator's reading after lane C's story-1 run: 284 (a)'s bar).
   */
  const armed = (st: GameState, us: PlayerState[], kept: boolean): boolean =>
    us.some(u => (u.grenades?.he ?? 0) > 0) || (kept && !!st.config.storyObjectives && us.some(u => strikeRestock(st, u) !== null));
  /**
   * DECISIONS 298 (e): one of the side's escort riders (core/storyObjectives.ts escortRiders) while the side has a destroy
   * objective standing — the destroy's, not the HQ strike's (megaplanet story-2: the HQ strike took them 3 s after the halt,
   * stood down, and they sat at the wheels for the rest of the match).
   */
  const escortRider = (st: GameState, sq: Squad): boolean => {
    if (!st.config.storyEscort) return false;
    const mine = (st.modeState.objectives ?? []).find(o => o.team === team && o.kind === 'destroy' && o.doneTick === undefined);
    const id = squadOf(st, sq.unitIds[0])?.id;
    return !!mine && id !== undefined && targetsLeft(st, mine).length > 0 && escortRiders(st, team).includes(id);
  };
  let objStriking: { squadIds: number[]; structureId: number; until: number } | null = null;
  /** 69.73 step 2b: does the side know structure `id` standing (in its record, not seen down or gone)? */
  const targetKnown = (st: GameState, id: number): boolean => { const e = knownStructure(st, team, id); return !!e && e.state === undefined; };
  const planObjectiveStrike = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number) => {
    if (!issue) return;
    const mine = (st.modeState.objectives ?? []).find(o => o.team === team && o.kind === 'destroy' && o.doneTick === undefined);
    const left = mine ? targetsLeft(st, mine) : [];
    const standDown = () => {
      if (!objStriking) return;
      for (const id of objStriking.squadIds) {
        const sq = byId.get(id);
        if (sq && alive(st, sq).length && !humanOrderFor(st, sq, tick) && !standoff.holds(sq.id)) issue(sq, plan.get(id) ?? { kind: 'free' }, 'base');
      }
      objStriking = null;
    };
    if (!left.length) { standDown(); return; }
    // (69.73 step 2b, with intelRecord: only the targets the side knows — seen, and not seen down — DECISIONS 303 (1): no
    // planner reads the truth for an enemy's place; none known, the search looks for them, planSearch)
    const targets = left.filter(t => !st.config.intelRecord || targetKnown(st, t.structureId)).map(t => st.structures.find(x => x.id === t.structureId)).filter((x): x is NonNullable<typeof x> => !!x);
    const planetIndex = targets[0]?.planetIndex ?? st.structures.find(x => x.id === left[0].structureId)?.planetIndex;   // (the objective's planet)
    if (planetIndex === undefined || (!targets.length && !st.config.intelRecord)) { standDown(); return; }
    // (69.79 step 3: a squad with someone crewing strikes with its people on foot, the crew kept at its seats — on story-1…8 at
    // 157 the side knew its target and planned no strike 52–77 % of the time, its idle squads each with a member at a driver's or
    // gun seat: a crewman, a support, a rifleman; all of it crewing, none)
    const strikeMembers = (us: PlayerState[]): PlayerState[] | null => {
      const seated = us.filter(u => crewing(st, u));
      if (!seated.length) return us;
      return seated.length < us.length ? us.filter(u => !crewing(st, u)) : null;
    };
    const centre = (sq: Squad): Vec3 => vecNormalize((strikeMembers(alive(st, sq)) ?? alive(st, sq)).reduce((a, u) => ({ x: a.x + u.surfaceDir.x, y: a.y + u.surfaceDir.y, z: a.z + u.surfaceDir.z }), { x: 0, y: 0, z: 0 }));
    const hqStrike = new Set(striking?.squadIds ?? []);
    // DECISIONS 298 (e): the escort's riders — its column halted at its point and they out of their bays (one at the APC's wheel
    // or kept on its .50 counts as out: the demolish gets a crew out), or already on the strike — take the destroy, whatever
    // their play holds them to: forward and otherwise idle (at 12 a side the side's four squads are committed from the start,
    // the strike one squad on foot far off). They join the squad on it, first in the pick.
    const rider = (sq: Squad): boolean => {
      if (!escortRider(st, sq)) return false;
      if (objStriking?.squadIds.includes(sq.id)) return true;
      return alive(st, sq).every(u => !u.mount || crewing(st, u));
    };
    const riders = squads.filter(sq => {
      // (423's story-5 at 576 s: a rider squad whose every living member crews — its one left alive at the technical's wheel —
      // has nobody on foot to send, so it is not fit; before, the issue's fallback named the whole squad, the driver with it)
      const us = alive(st, sq), foot = strikeMembers(us);
      return rider(sq) && !!foot && foot.length > 0 && armed(st, foot, !!objStriking?.squadIds.includes(sq.id)) && !humanOrderFor(st, sq, tick) && !hqStrike.has(sq.id) && us.every(u => u.planetIndex === planetIndex) && claimOf(st, sq, tick) >= TASK_RANK.play;   // (69.72 step 5: nothing above its play — no member's errand)
    });
    const fitWith = (seatedCrew: boolean) => squads.filter(sq => {
      if (riders.includes(sq)) return true;
      const us = alive(st, sq), o = plan.get(sq.id), foot = seatedCrew ? strikeMembers(us) : us.some(u => crewing(st, u)) ? null : us;
      // (69.61: a squad already striking stays on it whatever this round's plan holds it to — in the trace the side's nearest
      // squad, 341 people from the site, was handed a hold and the strike went to one 2,075 back)
      const striking = !!objStriking?.squadIds.includes(sq.id);
      // (69.61 step 4: never a squad on a carry run — on the ¼ board the strike at the site took the squad sent for the intel)
      return !!foot && foot.length > 0 && armed(st, foot, striking) && (o?.kind !== 'hold' || striking || searching?.squadId === sq.id) && !hqStrike.has(sq.id) && foot.every(u => u.planetIndex === planetIndex) && (striking || searching?.squadId === sq.id || free(st, sq, tick));   // (69.72 step 5: a squad no carry, play, build or human holds, or already on it — its strike or its search)
    });
    // (69.73 step 2b, with intelRecord: none of its targets known — the strike's squads search for them, planSearch; 69.79
    // step 3: a squad with a crewman seated is not one of them — the search's walk is the whole squad's)
    if (!targets.length) { standDown(); planSearch(st, plan, tick, dt, fitWith(false)); searchedThisRound = true; return; }
    const fit = fitWith(true);
    if (!fit.length) { standDown(); return; }
    const r = st.planets[targets[0].planetIndex].radius;
    const nearest = (sq: Squad) => targets.reduce((best, t) => (arcBetween(centre(sq), t.surfaceDir, r) < arcBetween(centre(sq), best.surfaceDir, r) ? t : best));
    const kept = objStriking ? fit.filter(sq => objStriking!.squadIds.includes(sq.id) && !riders.includes(sq)) : [];
    const others = fit.filter(sq => !riders.includes(sq));
    const pick = [...riders, ...(kept.length ? kept : [...others].sort((a, b) => arcBetween(centre(a), nearest(a).surfaceDir, r) - arcBetween(centre(b), nearest(b).surfaceDir, r) || a.id - b.id)).slice(0, STORY.strikeSquads)];
    const target = nearest(pick[0]);
    const ids = pick.map(sq => sq.id);
    const fresh = !objStriking || objStriking.structureId !== target.id || ids.some(id => !objStriking!.squadIds.includes(id));
    if (fresh && objStriking) standDown();
    const renew = Math.max(1, Math.round(replanSeconds / dt));
    if (fresh || tick + renew >= objStriking!.until) {
      const until = tick + Math.max(1, Math.round(STORY.strikeSeconds / dt));
      for (const sq of pick) {
        // (69.79 step 3: its members on foot named when its crewman stays seated — a rider's too: its crew at the wheel or the
        // .50 counts as out for the pick, but the demolish does not get it out (415, storyLab7 strikeCrews on story-5: the
        // riders re-struck every 10 s at 210–240 s with the driver seated, the strike named to all))
        const us = alive(st, sq), foot = strikeMembers(us) ?? us;
        issue(sq, { kind: 'demolish', structureId: target.id }, 'task', until, foot.length < us.length ? foot.map(u => u.id) : undefined, claimFor(st, sq, tick, 'objStrike'));   // (a rider's: its play's next)
      }
      objStriking = { squadIds: ids, structureId: target.id, until };
    }
  };

  /**
   * 69.61 step 4 (GameConfig.storyCarry): the carries — the side's own item at rest, the nearest fit squad sent to it (whoever
   * comes within reach takes it up); carried by one of ours, the carrier's squad sent home with it; the other side's item (ours
   * to keep) carried, or at rest away from its point, the nearest fit squad sent after it — to stop the carrier, or to return
   * it. The item's place is the beacon both sides see (the HUD's line). Move tasks of STORY.carrySeconds, re-planned each
   * round; a squad no longer wanted stood down to its plan's order.
   */
  let carrying: number[] = [];
  // 415 step 6d: the carry's proposal — read only: the squads to send at the side's item (its bearer home, the nearest fit
  // squad to a loose one) and at the enemy's loose item, the bearers its lift holds, and the wave ship to call for one
  const carryProposal = (st: GameState, tick: number): CarryProposal => {
    const list = st.modeState.objectives ?? [];
    const mine = list.find(o => o.team === team && o.kind === 'carry' && o.doneTick === undefined && o.item);
    const theirs = list.find(o => o.team !== team && o.kind === 'carry' && o.doneTick === undefined && o.item);
    const home = st.board?.bases.find(b => b.team === team);
    const centre = (sq: Squad): Vec3 => vecNormalize(alive(st, sq).reduce((a, u) => ({ x: a.x + u.surfaceDir.x, y: a.y + u.surfaceDir.y, z: a.z + u.surfaceDir.z }), { x: 0, y: 0, z: 0 }));
    const fit = (sq: Squad, planetIndex: number) => { const us = alive(st, sq); return us.length > 0 && (free(st, sq, tick) || carrying.includes(sq.id)) && !standoff.holds(sq.id) && us.every(u => u.planetIndex === planetIndex) && !us.some(u => crewing(st, u)) && searching?.squadId !== sq.id; };   // (69.72 step 5: no strike, play or human holds it; 69.73 step 2b: nor the searcher)
    const sends: Array<{ sq: Squad; order: SquadOrder }> = [], lifted: number[] = [];
    let pickup: CarryProposal['pickup'] = null;
    const nearestTo = (planetIndex: number, dir: Vec3): Squad | null => {
      const r = st.planets[planetIndex]?.radius ?? 1;
      return squads.filter(sq => fit(sq, planetIndex) && !sends.some(x => x.sq.id === sq.id) && !lifted.includes(sq.id)).sort((a, b) => arcBetween(centre(a), dir, r) - arcBetween(centre(b), dir, r) || a.id - b.id)[0] ?? null;
    };
    if (mine?.item) {
      const it = mine.item;
      const bearer = it.carrierId !== undefined ? squads.find(sq => sq.unitIds.includes(it.carrierId!)) : undefined;
      if (bearer && home && !humanOrderFor(st, bearer, tick)) {
        // (69.61 step 6: far from home, the side's wave ship fetched for it — the Match holds the squad at its ramp till it
        // lifts; refused (the ship away, no room), it walks and asks again next round; a transport in reach rides it, the APC loop's)
        const mid = squadOf(st, bearer.unitIds[0])?.id, R = st.planets[it.planetIndex]?.radius ?? 1;
        const far = it.planetIndex !== home.planetIndex || arcBetween(it.surfaceDir, home.dir, R) > STORY.carryRidePeople * PERSON_BODY_HEIGHT * personSetOf(st).k;
        const fetching = mid !== undefined && (st.dropships?.flights ?? []).some(f => f.pickup?.squadId === mid);
        const call = !!drops?.pickup && far && mid !== undefined && !fetching;
        if (call) pickup = { squadId: mid!, at: { planetIndex: it.planetIndex, surfaceDir: it.surfaceDir } };
        // (69.72 step 5, DECISIONS 304: fetched, or being called for, the squad is its lift's — rank 2 — so no move of the
        // carry's goes over its hold at the ramp: the move walked it off the ramp before, and is refused now)
        if (call || fetching) lifted.push(bearer.id);
        else sends.push({ sq: bearer, order: { kind: 'move', planetIndex: home.planetIndex, surfaceDir: home.dir } });
      }
      else if (it.carrierId === undefined) { const q = nearestTo(it.planetIndex, it.surfaceDir); if (q) sends.push({ sq: q, order: { kind: 'move', planetIndex: it.planetIndex, surfaceDir: it.surfaceDir } }); }
    }
    if (theirs?.item) {
      const it = theirs.item, from = st.modeState.points?.sites.find(x => x.id === theirs.pointId);
      const r = st.planets[it.planetIndex]?.radius ?? 1, reach = STORY.carryReachPeople * PERSON_BODY_HEIGHT * personSetOf(st).k;
      const loose = it.carrierId !== undefined || !from || from.planetIndex !== it.planetIndex || arcBetween(it.surfaceDir, from.surfaceDir, r) > reach;
      if (loose) { const q = nearestTo(it.planetIndex, it.surfaceDir); if (q) sends.push({ sq: q, order: { kind: 'move', planetIndex: it.planetIndex, surfaceDir: it.surfaceDir } }); }
    }
    return { sends, lifted, pickup };
  };

  // the carry (69.61 step 4): its proposal taken — the ship called, the squads it no longer wants back on their base
  // orders, the sends tasked
  const planCarry = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number) => {
    if (!issue) return;
    const { sends, lifted, pickup } = carryProposal(st, tick);
    if (pickup) drops!.pickup!(pickup.squadId, pickup.at);
    for (const id of carrying) {
      if (sends.some(x => x.sq.id === id) || lifted.includes(id)) continue;
      const sq = squads.find(x => x.id === id);
      if (sq && alive(st, sq).length && !humanOrderFor(st, sq, tick) && !standoff.holds(sq.id)) issue(sq, plan.get(id) ?? { kind: 'free' }, 'base');
    }
    const until = tick + Math.max(1, Math.round(STORY.carrySeconds / dt));
    for (const x of sends) issue(x.sq, x.order, 'task', until, undefined, claimFor(st, x.sq, tick, 'carry'));   // (a bearer in a play: its play's next)
    carrying = [...sends.map(x => x.sq.id), ...lifted];
  };

  /**
   * 69.79 step 4 (the coordinator; the hold trace on 163, story-2 t1, 3 t1, 7 t0, 8 t1: the squads the routine sent to the
   * side's hold were pulled off 40–56 % of the time — to the strike's search and the carry, which take any squad on the
   * routine — and a point won was lost back with nobody in its circle, story-3's t1 at 109 of 120 s): once the side owns its
   * hold's point, one squad is claimed there (`hold`, rank 4.9: over the carry, the strikes and the search; under a play,
   * an errand, a lift or a human) until the hold is done or the point lost. The squad kept while fit; else the fit squad
   * with the fewest crewing, one on the routine before one on the carry or a strike's search, then the nearest — not the
   * carry's bearer, nor a strike at its target. Taken from the carry, a strike or the search, it leaves their lists (their
   * stand-downs would give it its base order, clearing the hold). Someone in it crewing, the hold names its people on foot.
   */
  const holders = new Map<number, number>();   // (objective id → its squad)
  const planHold = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number) => {
    if (!issue) return;
    const sites = st.modeState.points?.sites ?? [];
    const bearer = (st.modeState.objectives ?? []).map(o => o.item?.carrierId).filter((x): x is number => x !== undefined);
    const until = tick + Math.max(1, Math.round((replanSeconds + 1) / dt));
    const taken = new Set<number>();
    for (const o of st.modeState.objectives ?? []) {
      if (o.team !== team || o.kind !== 'hold' || o.pointId === undefined) continue;
      const site = sites.find(x => x.id === o.pointId);
      if (o.doneTick !== undefined || !site || site.owner !== team) { holders.delete(o.id); continue; }
      const r = st.planets[site.planetIndex]?.radius ?? 1;
      // (what the hold may take a squad from: the routine, the carry (not its bearer), a strike's search, the search)
      const takes = (sq: Squad): boolean => {
        if (holders.get(o.id) === sq.id) return true;
        const c = claimOf(st, sq, tick);
        if (c >= TASK_RANK.routine) return true;
        if (c < TASK_RANK.objectives) return false;
        const ts = (squadOf(st, sq.unitIds[0])?.tasks ?? []).filter(x => x.claim !== undefined && (x.expires === undefined || tick < x.expires));
        return ts.every(x => x.claim === 'carry' || x.claim === 'search' || (x.claim === 'objStrike' && x.order.kind === 'move'));
      };
      const fit = squads.filter(sq => {
        const us = alive(st, sq);
        return us.length > 0 && !taken.has(sq.id) && us.every(u => u.planetIndex === site.planetIndex) && us.some(u => !crewing(st, u)) && !us.some(u => bearer.includes(u.id))
          && !humanOrderFor(st, sq, tick) && !standoff.holds(sq.id) && takes(sq);
      });
      if (!fit.length) { holders.delete(o.id); continue; }
      const at = (sq: Squad) => Math.min(...alive(st, sq).map(u => arcBetween(u.surfaceDir, site.surfaceDir, r)));
      const crews = (sq: Squad) => alive(st, sq).filter(u => crewing(st, u)).length;
      const sq = fit.find(x => x.id === holders.get(o.id))
        ?? [...fit].sort((a, b) => crews(a) - crews(b) || Number(!free(st, a, tick)) - Number(!free(st, b, tick)) || at(a) - at(b) || a.id - b.id)[0];
      holders.set(o.id, sq.id);
      taken.add(sq.id);
      // (out of the carry's, the strikes' and the search's lists: theirs no longer)
      carrying = carrying.filter(id => id !== sq.id);
      if (objStriking?.squadIds.includes(sq.id)) objStriking = { ...objStriking, squadIds: objStriking.squadIds.filter(id => id !== sq.id) };
      if (searching?.squadId === sq.id) searching = null;
      const us = alive(st, sq), foot = us.filter(u => !crewing(st, u));
      issue(sq, { kind: 'hold', pointId: site.id }, 'task', until, foot.length < us.length ? foot.map(u => u.id) : undefined, { claim: 'hold' });
    }
  };

  /**
   * 69.61 step 5 (GameConfig.storyEscort, with mountedAssault): the side's escort objective — a column marched to its point.
   * With no live play of ours there and a combined force ready (core/plays.ts mountedReady, read from the base: an APC no
   * play rides and a fire-support vehicle), the two fit squads nearest home (not a human's, not striking, carrying or
   * crewing, in no live play, no task under way) become a mounted play at the point, formed at home so the assault boards there; its convoy is
   * the column (core/storyObjectives.ts reads its halt). Stopped short, the play goes on on foot; the next forms once it is over.
   */
  const escortOf = (st: GameState) => (st.modeState.objectives ?? []).find(o => o.team === team && o.kind === 'escort' && o.doneTick === undefined && o.pointId !== undefined);
  /**
   * A squad in a live mounted play of ours (69.61 step 5's guard for the escort's play; 69.37's fix, lane E's finding 4: every
   * mounted play's — the strikes' demolish tasks took the assault of the plays the plays' own rule forms within 10–40 s on the
   * ½ board), kept off every other errand: the strikes, the carries, the efforts, the builds, the tactics, the re-plan. The plays
   * name the Match's squads.
   */
  /** The escort's own vehicles: the side's APCs no live mounted play rides and its tanks and technicals (not a mortar) — core/plays.ts mountedForces' kinds. */
  const escortVehicles = (st: GameState): Set<number> => {
    const ridden = new Set((st.plays ?? []).filter(q => q.phase !== 'done' && q.mounted).flatMap(q => Object.values(q.mounted!.rides)));
    return new Set(st.structures.filter(v => v.health > 0 && structureTeam(st, v) === team && v.armament !== 'mortar'
      && (((v.type === 'wheeled' || v.type === 'tracked') && !ridden.has(v.id)) || v.type === 'tank' || v.type === 'technical')).map(v => v.id));
  };
  const planEscort = (st: GameState, tick: number) => {
    const o = escortOf(st), home = st.board?.bases.find(b => b.team === team);
    const site = o ? st.modeState.points?.sites.find(x => x.id === o.pointId) : undefined;
    if (!o || !home || !site) return;
    if ((st.plays ?? []).some(p => p.team === team && p.phase !== 'done' && 'pointId' in p.objective && p.objective.pointId === site.id)) return;
    if (!mountedForce(st, team)) return;   // (a column whatever the distance: not mountedReady's walk — story-6…8's points inside it were never escorted)
    const inPlay = new Set((st.plays ?? []).filter(p => p.team === team && p.phase !== 'done').flatMap(p => [...p.support, ...p.assault]));
    const r = st.planets[home.planetIndex].radius;
    const centre = (sq: Squad): Vec3 => vecNormalize(alive(st, sq).reduce((a, u) => ({ x: a.x + u.surfaceDir.x, y: a.y + u.surfaceDir.y, z: a.z + u.surfaceDir.z }), { x: 0, y: 0, z: 0 }));
    // (69.61, lane C's lab-7 trace on large: the column's play dissolved at 60 s and never came back — from then every squad not
    // striking sat at a wheel or a gun, the play's own leavings among them: the support's in the tank, the assault's gunner on the
    // 6×6's .50. A crew in the escort's own vehicles — an APC no live play rides, a tank or a technical — is fit: the play takes
    // its rides and covers with those vehicles as it formed the first time. The convoy's truck and the mortar keep their crews.
    // A squad on foot goes first.)
    const own = escortVehicles(st);
    const seated = (sq: Squad) => alive(st, sq).filter(u => crewing(st, u)).length;
    const fit = squads.filter(sq => { const us = alive(st, sq); return us.length > 0 && free(st, sq, tick) && !standoff.holds(sq.id) && !inPlay.has(squadOf(st, sq.unitIds[0])?.id ?? -1) && !squadOf(st, sq.unitIds[0])?.tasks.some(t => t.expires === undefined || t.expires > tick) && us.every(u => u.planetIndex === home.planetIndex) && us.every(u => !crewing(st, u) || own.has(u.mount!.bodyId)); });
    const pick = fit.sort((a, b) => seated(a) - seated(b) || arcBetween(centre(a), home.dir, r) - arcBetween(centre(b), home.dir, r) || a.id - b.id).slice(0, 2);
    if (pick.length === 2) formPlay!(pick, site.id, { mounted: true });
  };

  /**
   * 68.22 step 4 (DIRECTION §0.9; X37's first cure for the blob): two squads or more of ours sent at one contested point —
   * the enemy holds it, or the team's picture puts an enemy within the support band of it — become one play there
   * (support and assault, core/plays.ts) instead of all walking on it; never with a squad a human orders (68.23), never
   * a second play of ours at the same point. 69.37 step 5 (DECISIONS 268 (2) (a), (b), as leaned; GameConfig.mountedAssault):
   * ordered mounted when a combined force is ready and the point lies beyond a walk (core/plays.ts mountedReady).
   */
  const planPlays = (st: GameState, plan: Map<number, SquadOrder>, tick: number, staging: ReadonlySet<number> = new Set()) => {
    const byPoint = new Map<number, Squad[]>();
    for (const squad of squads) {
      const o = plan.get(squad.id);
      if (!o || (o.kind !== 'take' && o.kind !== 'hold') || o.pointId === undefined) continue;
      if (alive(st, squad).length === 0 || !free(st, squad, tick) || standoff.holds(squad.id)) continue;   // (66.15: not a manoeuvre's squad; 69.72 step 5: none another claims)
      // (69.61: nor a squad on a carry run — story-4 ½, 18 plays in 180 s — 69.72 step 5: the carry's claim, above)
      if (searching?.squadId === squad.id) continue;   // (69.73 step 2b: nor the searcher)
      byPoint.set(o.pointId, [...(byPoint.get(o.pointId) ?? []), squad]);
    }
    const band = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance) * PERSON_BODY_HEIGHT * personSetOf(st).k;
    const picture = byPoint.size ? side.picture() : [];
    for (const [pointId, group] of [...byPoint].sort((a, b) => a[0] - b[0])) {
      if (group.length < 2) continue;
      if (staging.has(pointId)) continue;   // 69.6c: its squads still converging — the play when they go together
      if ((st.plays ?? []).some(p => p.team === team && p.phase !== 'done' && 'pointId' in p.objective && p.objective.pointId === pointId)) continue;
      // 69.6c: a play there dissolved in its quiet setting lately — the squads take it on their orders meanwhile
      if ((st.plays ?? []).some(p => p.team === team && p.dissolvedQuiet !== undefined && 'pointId' in p.objective && p.objective.pointId === pointId && (tick - p.dissolvedQuiet) * PHYS_DT < PLAY_QUIET_REFORM_SECONDS)) continue;
      const site = st.modeState.points?.sites.find(x => x.id === pointId);
      if (!site) continue;
      const r = st.planets[site.planetIndex].radius;
      const contested = (site.owner !== null && site.owner !== team) || picture.some(b => b.planetIndex === site.planetIndex && arcBetween(b.surfaceDir, site.surfaceDir, r) <= band);
      if (!contested) continue;
      // (69.37 step 5: mounted, with a combined force ready and the point beyond a walk from the nearest of the group's people)
      const mounted = !!st.config.mountedAssault && mountedReady(st, team, group.flatMap(sq => alive(st, sq).map(u => u.surfaceDir)), { planetIndex: site.planetIndex, surfaceDir: site.surfaceDir });
      const opts = { ...(flankOn(st, team) ? { flank: true as const } : {}), ...(mounted ? { mounted: true as const } : {}) };
      formPlay!(group, pointId, Object.keys(opts).length ? opts : undefined);   // 66.16: the assault routes round by dead ground (69.37: a mounted one's squads held from now)
    }
  };

  /**
   * 69.2e (Foothold's intel loop): the commander grows its web toward where it sends its squads — for a point its web does
   * not reach (no linked node within link range of it), one squad with relays left (nearest, not a human's, no build under
   * way on the side) is told to plant a relay at the first linked spot toward it (0.9 of the link range from the nearest
   * linked node, shorter where that ground cannot be built on), its first member on foot planting.
   * 69.4: relays first, while the web does not reach a target; then a FOB, once the web reaches within link range of a
   * target more than FOB.minFromBase people from the side's HQ with none of the side's FOBs within FOB.spacing of it — at
   * the first spot toward it the same way (0.9 → 0.7 → 0.5 of the link range, never past the target), from the side's count,
   * planted by the nearest squad not a human's with a member on foot. One build a re-plan across both.
   */
  const planBuilds = (st: GameState, plan: Map<number, SquadOrder>, tick: number) => {
    const mine = (st.squads ?? []).filter(q => q.team === team);
    if (mine.some(q => q.tasks.some(t => t.order.kind === 'build' && (t.expires === undefined || t.expires > tick)))) return;   // one under way
    const linked = new Set(st.webs?.[team]?.linked ?? linkedIds(st, team));
    const nodes = st.structures.filter(x => linked.has(x.id));
    if (!nodes.length) return;
    const reach = relayLink(st), person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    const targets = [...new Set([...plan.values()].map(o => ((o.kind === 'take' || o.kind === 'hold') && o.pointId !== undefined ? o.pointId : null)).filter((x): x is number => x !== null))].sort((a, b) => a - b);
    const sites = targets.map(id => st.modeState.points?.sites.find(x => x.id === id)).filter((x): x is CapturePoint => !!x);
    // the nearest linked node to a site on its planet, how far, and the first spot toward it a kind can stand on
    const nearest = (site: CapturePoint) => {
      const r = st.planets[site.planetIndex].radius;
      const onPlanet = nodes.filter(nd => nd.planetIndex === site.planetIndex);
      if (!onPlanet.length) return null;
      const from = onPlanet.reduce((best, nd) => (arcBetween(nd.surfaceDir, site.surfaceDir, r) < arcBetween(best.surfaceDir, site.surfaceDir, r) ? nd : best));
      return { from, r, d: arcBetween(from.surfaceDir, site.surfaceDir, r) };
    };
    // 69.5b's open note (lane A's probe, scripts/buildStalls.ts: 1172 of 1969 relay spots refused): under Operations a kind
    // that stands only behind the front (a relay with drops, a FOB: 69.6f) can't stand in the sector in play, where the line
    // toward an objective in it mostly ends — so first the farthest spot on that line short of it, in 5-person steps
    const shortOfPlay = (kind: 'relay' | 'fob', site: CapturePoint, from: { surfaceDir: Vec3 }, r: number, span: number): Vec3[] => {
      const front = st.modeState.points?.front;
      if (!front || !st.modeState.points?.push || front.planetIndex !== site.planetIndex || !frontSiteKind(kind, st.config)) return [];
      const step = (5 * person) / r;
      let last: Vec3 | null = null;
      for (let a = step; a <= span / r; a += step) {
        const dir = stepToward(from.surfaceDir, site.surfaceDir, a);
        if (sectorInPlay(st, sectorAt(front, dir))) return last ? [last] : [];
        last = dir;
      }
      return [];   // (the line never enters the sector in play: the usual spots)
    };
    const spotToward = (kind: 'relay' | 'fob', site: CapturePoint, from: { surfaceDir: Vec3 }, r: number, d: number) => {
      const span = Math.min(reach, kind === 'fob' ? d : reach);
      return [...shortOfPlay(kind, site, from, r, span), ...[0.9, 0.7, 0.5].map(f => stepToward(from.surfaceDir, site.surfaceDir, (f * span) / r))]
        .find(dir => buildRefusal(st, kind, { planetIndex: site.planetIndex, surfaceDir: dir }, team) === null);
    };
    const send = (kind: 'relay' | 'fob', site: CapturePoint, spot: Vec3): boolean => {
      const r = st.planets[site.planetIndex].radius;
      const builders = squads.filter(sq => {
        const ms = squadOf(st, sq.unitIds[0]);
        return !!ms && (kind === 'fob' || (ms.relays ?? 0) > 0) && free(st, sq, tick) && !standoff.holds(sq.id) && alive(st, sq).some(u => !u.mount) && searching?.squadId !== sq.id
          && !objStriking?.squadIds.includes(sq.id);   // (and no member already on an errand; 69.73 step 2b: nor the searcher; 415: nor a squad on the objective's strike — its target fallen, its claim lapses until the strike planner, later in the round, sends it at the next: story-5's sq0 planted at 160 s and was struck the same tick, its planter named)
        // (69.61 step 5: a relay's build took the column's assault at its start; 69.42's churn: any play's — a build's planter
        // ended a play the round it formed; 69.61: and a planter walked off a strike at the site — 69.72 step 5: the plays',
        // strikes' and carry's claims, `free`)
      });
      if (!builders.length) return false;
      const near = (sq: Squad) => Math.min(...alive(st, sq).map(u => arcBetween(u.surfaceDir, spot, r)));
      const who = builders.reduce((best, sq) => (near(sq) < near(best) ? sq : best));
      const planter = alive(st, who).find(u => !u.mount)!;
      issue?.(who, { kind: 'build', structure: kind, planetIndex: site.planetIndex, surfaceDir: spot }, 'task', undefined, [planter.id]);
      return true;
    };
    // relays first: a target the web does not reach
    for (const site of sites) {
      const n = nearest(site);
      if (!n || n.d <= reach) continue;   // reached already
      const spot = spotToward('relay', site, n.from, n.r, n.d);
      if (!spot) continue;
      if (send('relay', site, spot)) return;   // one a re-plan
      break;   // nobody to plant a relay: no squad has one left
    }
    // 69.4: then a FOB, where the web reaches a far target with none of the side's FOBs near it
    if (!fobsOn(st.config) || fobsLeftFor(st, team) <= 0) return;
    const hqs = st.structures.filter(x => x.type === 'ecotourism' && x.health > 0 && structureTeam(st, x) === team);
    const fobs = st.structures.filter(x => x.type === 'fob' && x.health > 0 && structureTeam(st, x) === team);
    for (const site of sites) {
      const n = nearest(site);
      if (!n || n.d > reach) continue;   // the web does not reach it yet (a relay's job)
      const home = hqs.filter(h => h.planetIndex === site.planetIndex);
      if (!home.length || Math.min(...home.map(h => arcBetween(h.surfaceDir, site.surfaceDir, n.r))) <= FOB.minFromBase * person) continue;   // near home: the base serves it
      if (fobs.some(f => f.planetIndex === site.planetIndex && arcBetween(f.surfaceDir, site.surfaceDir, n.r) <= FOB.spacing * person)) continue;   // one serves it already
      // 69.4 (lane A's probe: the forward node stands at the front's very edge, so the line gives no FOB spot short of
      // play): else spots behind that node toward the side's home, 10 / 20 / 40 people back — its own held ground, in link
      const back = home.reduce((a, h) => (arcBetween(h.surfaceDir, n.from.surfaceDir, n.r) < arcBetween(a.surfaceDir, n.from.surfaceDir, n.r) ? h : a));
      const spot = spotToward('fob', site, n.from, n.r, n.d) ?? [10, 20, 40].map(p => stepToward(n.from.surfaceDir, back.surfaceDir, (p * person) / n.r))
        .find(dir => buildRefusal(st, 'fob', { planetIndex: site.planetIndex, surfaceDir: dir }, team) === null);
      if (spot && send('fob', site, spot)) return;
    }
  };

  /**
   * 69.6c: the squads converge on the active sector (CONVERGE). Returns the points whose squads are still staging (no
   * play forms there meanwhile). The waiting squads' holds are tasks lapsing by the next re-plan, re-given while they wait.
   */
  let stageSince: number | null = null;
  const planConverge = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number): Set<number> => {
    const staging = new Set<number>();
    if (!st.modeState.points?.push || !issue) { stageSince = null; return staging; }
    const live = activeSites(st);
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    const group = squads.flatMap(sq => {
      const o = plan.get(sq.id);
      const site = o && o.kind === 'take' ? live.find(x => x.id === o.pointId) : undefined;
      const dir = squadDir(st, sq);
      return site && dir && alive(st, sq).length >= 2 && free(st, sq, tick) && !standoff.holds(sq.id) ? [{ sq, site, dir }] : [];
    });
    if (group.length < 2) { stageSince = null; return staging; }
    const picture = side.picture();
    const ready = (g: (typeof group)[number]) => {
      const r = st.planets[g.site.planetIndex].radius;
      if (arcBetween(g.dir, g.site.surfaceDir, r) <= (CONVERGE.stagePeople + CONVERGE.readyPeople) * person) return true;
      return alive(st, g.sq).some(u => picture.some(b => b.planetIndex === u.planetIndex && arcBetween(b.surfaceDir, u.surfaceDir, r) <= CONVERGE.contactPeople * person));
    };
    if (group.every(ready)) { stageSince = null; return staging; }
    if (stageSince === null) stageSince = tick;
    if ((tick - stageSince) * dt >= CONVERGE.maxSeconds) return staging;   // waited long enough: go
    const until = tick + Math.round((replanSeconds + 1) / PHYS_DT);
    for (const g of group) {
      staging.add(g.site.id);
      if (!ready(g)) continue;   // still coming: its take walks it up
      const r = st.planets[g.site.planetIndex].radius;
      const inContact = alive(st, g.sq).some(u => picture.some(b => b.planetIndex === u.planetIndex && arcBetween(b.surfaceDir, u.surfaceDir, r) <= CONVERGE.contactPeople * person));
      if (inContact) continue;   // fighting: it fights
      if (arcBetween(g.dir, g.site.surfaceDir, r) < (CONVERGE.stagePeople - CONVERGE.readyPeople) * person) continue;   // in already: never pulled back
      const at = stepToward(g.site.surfaceDir, g.dir, (CONVERGE.stagePeople * person) / r);
      issue(g.sq, { kind: 'hold', planetIndex: g.site.planetIndex, surfaceDir: at }, 'task', until);
    }
    return staging;
  };

  /**
   * 68.25: the stalemate's gun support (GUN_SUPPORT_PLAN). A point the plan sends a squad of ours to, not ours, with one of
   * ours within `fightPeople` of it, for `stalemateSeconds` running; an APC of the side idle (standing,
   * armed, nobody aboard, no order naming it, nobody walking to board it) on its planet; a squad of ours all within
   * `crewPeople` of it (two on foot at the least, every living member on its planet, not a human's, not riding) — the nearest is given a ride task in that APC
   * to the .50's position over the point. Never with the side kept on foot. One a stalemate: a point that fell quiet and
   * stalls again may have another.
   */
  const contestedSince = new Map<number, number>();
  const gunSent = new Set<number>();
  let gunTask: { squadId: number; until: number } | null = null;
  const planGunSupport = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number) => {
    const sites = st.modeState.points?.sites ?? [];
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    const targets = new Set([...plan.values()].map(o => ((o.kind === 'take' || o.kind === 'hold') && o.pointId !== undefined ? o.pointId : null)).filter((x): x is number => x !== null));
    const ours = st.players.filter(p => p.alive && p.teamIndex === team);
    for (const site of sites) {
      // stalled: a point the plan sends us to, not ours, with one of us in the fight for it (within the APC's contact
      // distance) — on Foothold the point in front is often neutral and its defenders unseen, so neither who holds it nor
      // the picture says "contested"; the front not moving does
      const r = st.planets[site.planetIndex]?.radius ?? 1;
      const stalled = targets.has(site.id) && site.owner !== team && ours.some(p => p.planetIndex === site.planetIndex && arcBetween(p.surfaceDir, site.surfaceDir, r) <= GUN_SUPPORT_PLAN.fightPeople * person);
      if (!stalled) { contestedSince.delete(site.id); gunSent.delete(site.id); continue; }
      if (!contestedSince.has(site.id)) contestedSince.set(site.id, tick);
    }
    if (gunTask && tick < gunTask.until) return;   // one at a time
    gunTask = null;
    if (vehiclesBarred(st, team)) return;
    const teamOf = (id: number) => st.players.find(p => p.id === id)?.teamIndex;
    const named = new Set((st.squads ?? []).flatMap(q => [q.base, ...q.tasks.map(t => t.order)]).map(o => (o && (o.kind === 'ride' || o.kind === 'crew') ? o.structureId : null)).filter((x): x is number => x !== null));
    // (the living of the side on foot only: a brain's plan outlives its unit's death and its boarding)
    const boarding = (id: number) => ours.some(p => !p.mount && (brainFace(st, p.id)?.plan?.dest?.key ?? '').startsWith(`board:${id}:`));
    // (63.1r, lane B's line: the technical is the fire-support hull — an idle one is taken before an idle APC, whose .50 is
    // the fallback; any armed hull of the side that drives, standing, nobody aboard, no order naming it, nobody boarding)
    // (69.42 (d), with munitionsSupply: a hull short of rounds — dry, or under a third — is counted out: it goes for its rounds, not to a base of fire)
    const idle = (st.structures ?? []).filter(s => (isTransport(s) || (s.type === 'technical' && s.health > 0 && drives(s))) && (s.seats ?? []).some(x => seatArmed(x)) && teamOf(s.ownerId) === team && (s.seats ?? []).every(x => !x.occupant) && !named.has(s.id) && !storesShort(st, s) && !boarding(s.id));
    if (!idle.length) return;
    for (const [pointId, since] of [...contestedSince].sort((a, b) => a[1] - b[1] || a[0] - b[0])) {
      if (gunSent.has(pointId) || (tick - since) * dt < GUN_SUPPORT_PLAN.stalemateSeconds) continue;
      const site = sites.find(s => s.id === pointId)!;
      const r = st.planets[site.planetIndex].radius;
      // the crew: the two of ours on foot nearest an idle APC within `crewPeople` of it — any squad's (lane B's units ride:
      // two fresh respawns at home will do; a whole squad was asked for before, and its driver was often 1,600 people off) —
      // bots not held by a human, their squads not under a human's order nor riding
      let best: { apc: (typeof idle)[number]; crew: PlayerState[]; d: number } | null = null;
      for (const apc of idle) {
        if (apc.planetIndex !== site.planetIndex) continue;
        const near = ours.filter(u => !u.mount && u.isBot && st.controllers?.[u.id] === undefined && u.planetIndex === apc.planetIndex)
          .filter(u => { const sq = squads.find(q => q.unitIds.includes(u.id)); return !sq || (errandFree(st, sq, tick) && !rideInForce(st, sq, tick)); })
          .map(u => ({ u, d: arcBetween(u.surfaceDir, apc.surfaceDir, r) }))
          .filter(x => x.d <= GUN_SUPPORT_PLAN.crewPeople * person)
          .sort((a, b) => a.d - b.d || a.u.id - b.u.id)
          .slice(0, 2);
        if (near.length < 2) continue;
        const d = near[1].d;
        // a technical with a crew in reach before any APC; then the nearer crew
        const tech = (x: { apc: (typeof idle)[number] }) => (x.apc.type === 'technical' ? 0 : 1);
        if (!best || tech({ apc }) < tech(best) || (tech({ apc }) === tech(best) && (d < best.d || (d === best.d && apc.id < best.apc.id)))) best = { apc, crew: near.map(x => x.u), d };
      }
      if (!best) continue;
      const pos = gunSupportPosition(st, site.planetIndex, site.surfaceDir, best.apc.surfaceDir, gunnerEyePeople(best.apc.type));
      if (!pos) continue;
      const until = tick + Math.round(GUN_SUPPORT_PLAN.seconds / PHYS_DT);
      const sq = squads.find(q => q.unitIds.includes(best!.crew[0].id)) ?? squads[0];
      // keep: 'crew' — at the position the driver keeps the hull and the gunner the .50 (lane B's ride keep), the base of fire
      issue!(sq, { kind: 'ride', structureId: best.apc.id, planetIndex: pos.planetIndex, surfaceDir: pos.dir, keep: 'crew' }, 'task', until, best.crew.map(u => u.id));
      gunSent.add(pointId);
      gunTask = { squadId: sq.id, until };
      gunSupports.push({ tick, pointId, squadId: sq.id, structureId: best.apc.id, kind: pos.kind, crew: best.crew.map(u => u.id) });
      return;   // one a re-plan
    }
  };
  const gunSupports: GunSupportRecord[] = [];

  /**
   * 69.5d step 3 (decision 145; the fall's consequences OPEN as 165): the adaptive commander's base strike. On the
   * Foothold board in a points battle (not under Operations: there the defenders' base is the last sector), each re-plan
   * it sends BASE_STRIKE.squads — its nearest squads to the enemy HQ not holding a point, not a human's, with an HE
   * grenade among them — to demolish the enemy's HQ when (a) its side is level or behind on tickets, (b) the team's
   * picture (the fairness rule's one accessor) puts fewer enemies within the support band of the HQ than the strike has
   * alive, and (c) the strike's walk to the HQ is at most BASE_STRIKE.walkRatio × its walk to its objective, or its web
   * reaches within link range of the HQ. It stands the strike down (the squads' base orders again, their tasks cleared)
   * when the HQ falls or a condition fails. Each strike sent or stood down goes in the order log (`strikes`) with why.
   */
  const strikes: StrikeRecord[] = [];
  let striking: { squadIds: number[]; structureId: number; until: number } | null = null;

  /**
   * 69.73 step 2b (DECISIONS 303 (2), the owner: "no info just milling around the surface till you bump into it"; with
   * intelRecord): while the side has not found the enemy's base, or knows none of its destroy objective's targets left, one
   * squad searches. The planet is cut into cells about INTEL.cellPeople apart (searchCells); the enemy's half is the cells
   * more than a quarter of the way round from the side's own base. Each round the cells any of the side's people came
   * within half a cell of are swept; the searcher — the fit squad nearest an unswept cell of that half — is sent to the
   * nearest such cell with a move (fighting as it goes), renewed every INTEL.searchSeconds, and sent
   * on when its cell is swept. Found (the record has it), the search stands down and the strikes take it. All of that half
   * swept and nothing found, it sweeps again. Each send and each find goes in the log (`searches`).
   * 69.79 (the coordinator's key case, lane G's 309 trace: story-7 still for 25 minutes): the base's searcher claims its
   * squad (`search`, rank 5.3) — as the commander's routine its move was cleared by the next replan's base order, at 10 s,
   * and the search believed it out until its 120 s ran down. A searcher no INTEL.progressPeople nearer its cell in
   * INTEL.stallSeconds (story-6 on v146: a squad reading its move, 229 people off, unmoving from 780 s to the end) is let go:
   * its cell counted swept, the squad rested INTEL.restSeconds, and the nearest other fit squad sent (`stalled` in the log).
   */
  let searching: { squadId: number; cell: number; until: number; best: number; bestTick: number } | null = null;
  const resting = new Map<number, number>();   // (69.79: a stalled searcher's squad, not picked again until this tick)
  const searchRank = taskRank({ order: { kind: 'free' }, by: 'commander', claim: 'search' });   // (69.79: the search's claim, 5.3)
  const swept = new Set<number>();
  const searches: SearchRecord[] = [];
  const found = { base: false, target: false };
  let cells: { planetIndex: number; dirs: Vec3[] } | null = null;
  let searchedThisRound = false;
  const planSearch = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number, strikers?: readonly Squad[]) => {
    if (!issue || !st.board) return;
    const home = st.board.bases.find(b => b.team === team), enemy = st.board.bases.find(b => b.team !== team);
    if (!home || !enemy) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k, r = st.planets[home.planetIndex].radius;
    if (!cells || cells.planetIndex !== home.planetIndex) cells = { planetIndex: home.planetIndex, dirs: searchCells(r, INTEL.cellPeople * person) };
    const half = cells.dirs.map((d, i) => ({ d, i })).filter(c => arcBetween(c.d, home.dir, r) > (Math.PI / 2) * r);
    for (const c of half) if (!swept.has(c.i) && st.players.some(u => u.alive && u.teamIndex === team && u.planetIndex === home.planetIndex && arcBetween(u.surfaceDir, c.d, r) <= 0.5 * INTEL.cellPeople * person)) swept.add(c.i);
    const sweptHere = half.filter(c => swept.has(c.i)).length;
    // what the side has found (logged once each)
    const mine = (st.modeState.objectives ?? []).find(o => o.team === team && o.kind === 'destroy' && o.doneTick === undefined);
    const baseFound = !!knownBase(st, team, enemy.team);
    const targetFound = !mine || targetsLeft(st, mine).some(t => targetKnown(st, t.structureId));
    if (baseFound && !found.base) { found.base = true; searches.push({ tick, swept: sweptHere, kind: 'found', what: 'base' }); }
    if (mine && targetFound && !found.target) { found.target = true; searches.push({ tick, swept: sweptHere, kind: 'found', what: 'target' }); }
    const standDown = () => {
      if (!searching) return;
      const sq = byId.get(searching.squadId);
      // (not one gone on to a strike or a carry since — its base order would clear that task too; the round's state predates it)
      const moved = !!sq && (!!objStriking?.squadIds.includes(sq.id) || !!striking?.squadIds.includes(sq.id) || carrying.includes(sq.id));
      if (sq && !moved && alive(st, sq).length && !humanOrderFor(st, sq, tick) && !standoff.holds(sq.id)) issue(sq, plan.get(sq.id) ?? { kind: 'free' }, 'base');
      searching = null;
    };
    if (baseFound && targetFound) { standDown(); return; }
    const centre = (sq: Squad): Vec3 => vecNormalize(alive(st, sq).reduce((a, u) => ({ x: a.x + u.surfaceDir.x, y: a.y + u.surfaceDir.y, z: a.z + u.surfaceDir.z }), { x: 0, y: 0, z: 0 }));
    // (69.79: the searcher's progress — its nearest yet to its cell; none in INTEL.stallSeconds, it is let go)
    if (searching) {
      const was = byId.get(searching.squadId);
      if (was && alive(st, was).length) {
        const d = arcBetween(centre(was), cells.dirs[searching.cell], r) / person;
        if (d < searching.best - INTEL.progressPeople) { searching.best = d; searching.bestTick = tick; }
        else if ((tick - searching.bestTick) * dt >= INTEL.stallSeconds) {
          searches.push({ tick, swept: sweptHere, kind: 'stalled', squadId: was.id, cell: searching.cell });
          swept.add(searching.cell);
          resting.set(was.id, tick + Math.round(INTEL.restSeconds / dt));
          standDown();
        }
      }
    }
    const rested = (sq: Squad) => (resting.get(sq.id) ?? -1) > tick;
    let open = half.filter(c => !swept.has(c.i));
    if (!open.length) { for (const c of half) swept.delete(c.i); open = half; }   // (all of it swept and nothing found: again)
    // (the objective strike's squads when it has nothing to strike — its riders and fit squads, with HE; else, for the base
    // alone, the strikes' guards — the search is the strike's walk while it has nothing to strike: not a crew, nor holding a
    // point — and 69.72 step 5 (DECISIONS 304), a squad nothing claims above the commander's routine (`free`: no human's,
    // carry's, strike's, play's or member's errand; its own search is the routine))
    // (69.79: its own searcher still fit while only the search's claim holds it — not once a strike or a play took it; a
    // rested one not)
    const baseFit = () => squads.filter(sq => {
      const us = alive(st, sq), o = plan.get(sq.id), mine = searching?.squadId === sq.id;
      if (!us.length || claimOf(st, sq, tick) < (mine ? searchRank : 6) || standoff.holds(sq.id) || rested(sq)) return false;
      if (us.some(u => u.planetIndex !== home.planetIndex || crewing(st, u))) return false;
      return o?.kind !== 'hold' || mine;
    });
    // (69.79 step 2, story-7's t0: the strike's own squads first; none fit — its HE out, its crews seated, the rest in plays,
    // which keep theirs (304) — the base search's guards: any free squad, HE or not, searches for the strike's targets)
    const strikeFit = strikers ? strikers.filter(sq => alive(st, sq).every(u => u.planetIndex === home.planetIndex) && !rested(sq)) : [];
    const fallback = !!strikers && !strikeFit.length;
    const fit = strikers && !fallback ? strikeFit : baseFit();
    // (step 3: looking for the base alone, with a rough area for it, the open cells inside its ring first — not when the
    // strike looks for its targets, which stand between the bases: with the ring first, the area setting found fewer of them)
    const area = baseArea(st, team, enemy.team);
    const inArea = area && !strikers ? open.filter(c => arcBetween(c.d, area.surfaceDir, r) <= area.spread!) : [];
    if (!baseFound && inArea.length) open = inArea;
    const nearestCell = (sq: Squad) => open.reduce((b, c) => (arcBetween(centre(sq), c.d, r) < arcBetween(centre(sq), b.d, r) ? c : b));
    const keep = searching ? fit.find(sq => sq.id === searching!.squadId) : undefined;
    const sq = keep ?? [...fit].sort((a, b) => arcBetween(centre(a), nearestCell(a).d, r) - arcBetween(centre(b), nearestCell(b).d, r) || a.id - b.id)[0];
    if (!sq) { standDown(); return; }
    if (searching && searching.squadId !== sq.id) standDown();
    const goOn = !searching || swept.has(searching.cell) || tick + Math.max(1, Math.round(replanSeconds / dt)) >= searching.until;
    if (!goOn) return;
    const cell = searching && !swept.has(searching.cell) ? cells.dirs[searching.cell] : nearestCell(sq).d;
    const idx = searching && !swept.has(searching.cell) ? searching.cell : nearestCell(sq).i;
    const until = tick + Math.max(1, Math.round(INTEL.searchSeconds / dt));
    // (a move, not a scout's: a scout's task ends when its place is seen, and a cell's middle is seen from far off; 304: the
    // objective strike's searcher keeps the strike's claim; 69.79: the base's searcher claims its squad for the search)
    issue(sq, { kind: 'move', planetIndex: home.planetIndex, surfaceDir: cell }, 'task', until, undefined, strikers && !fallback ? claimFor(st, sq, tick, 'objStrike') : { claim: 'search' });   // (69.79 step 2: a fallback searcher is the search's, not the strike's)
    const fresh = !searching || searching.cell !== idx || searching.squadId !== sq.id;
    if (!searching || searching.cell !== idx) searches.push({ tick, swept: sweptHere, kind: 'sent', squadId: sq.id, cell: idx });
    // (69.79: a new cell or searcher starts its progress from where it stands)
    searching = fresh ? { squadId: sq.id, cell: idx, until, best: arcBetween(centre(sq), cell, r) / person, bestTick: tick } : { ...searching!, until };
  };
  // 415 step 6d: the HQ strike's proposal — read only (no order issued, the strike's record untouched): the squads to send,
  // or why the side stands its strike down; null, no strike to weigh (no board, no points, or a front's push)
  const hqStrikeProposal = (st: GameState, plan: ReadonlyMap<number, SquadOrder>, tick: number, side: SideRead): HqStrikeProposal | null => {
    const ps = st.modeState.points;
    if (!st.board || !ps || (ps.front && ps.push)) return null;
    const enemy = st.board.bases.find(b => b.team !== team);
    // (69.73 step 2b, with intelRecord: the HQ as the side knows it — not found yet, no strike (the search looks); seen down, fallen)
    const hq = enemy?.hq !== undefined && (!st.config.intelRecord || targetKnown(st, enemy.hq)) ? st.structures.find(x => x.id === enemy.hq && x.health > 0) : undefined;
    const tickets = st.modeState.respawn?.tickets ?? {};
    const ours = tickets[team] ?? 0, theirs = enemy ? tickets[enemy.team] ?? 0 : 0;
    if (!enemy || !hq) { return { kind: 'standDown', why: st.config.intelRecord && enemy?.hq !== undefined && !knownStructure(st, team, enemy.hq) ? 'the HQ not found' : 'the HQ fell', line: { tickets: [ours, theirs], defenders: 0, strength: 0, walk: null, webReach: false } }; }
    const r = st.planets[hq.planetIndex].radius;
    const centre = (sq: Squad): Vec3 => vecNormalize(alive(st, sq).reduce((a, u) => ({ x: a.x + u.surfaceDir.x, y: a.y + u.surfaceDir.y, z: a.z + u.surfaceDir.z }), { x: 0, y: 0, z: 0 }));
    const fit = squads.filter(sq => {
      const us = alive(st, sq);
      const o = plan.get(sq.id);
      // (69.61 step 4: never a squad on a carry run — on the ¼ board the strike took the squad sent for the intel; DECISIONS
      // 298 (e): nor the escort's riders while the side's destroy stands)
      // (69.61, with the story on: never a vehicle's crew, as the story's own strike — lab-7 ½: the technical's driver and gunner,
      // sent at the HQ, sat in it parked 1,250 people off from 660 s to the end)
      return us.length > 0 && us.every(u => u.planetIndex === hq.planetIndex) && us.some(u => (u.grenades?.he ?? 0) > 0) && o?.kind !== 'hold' && (free(st, sq, tick) || !!striking?.squadIds.includes(sq.id)) && !escortRider(st, sq) && !(st.modeState.objectives && us.some(u => crewing(st, u)));   // (69.72 step 5: none another claims)
    });
    const toHq = (sq: Squad) => arcBetween(centre(sq), hq.surfaceDir, r);
    // the strike: the squads already on it while still fit, else the nearest fit ones
    const kept = striking ? fit.filter(sq => striking!.squadIds.includes(sq.id)) : [];
    const pick = (kept.length ? kept : [...fit].sort((a, b) => toHq(a) - toHq(b) || a.id - b.id)).slice(0, BASE_STRIKE.squads);
    const band = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance) * PERSON_BODY_HEIGHT * personSetOf(st).k;
    const defenders = side.picture().filter(b => b.planetIndex === hq.planetIndex && arcBetween(b.surfaceDir, hq.surfaceDir, r) <= band).length;
    const strength = pick.reduce((n, sq) => n + alive(st, sq).length, 0);
    const lead = pick[0];
    const o = lead ? plan.get(lead.id) : undefined;
    const site = o && 'pointId' in o && o.pointId !== undefined ? ps.sites.find(x => x.id === o.pointId) : undefined;
    const toObj = lead && site && site.planetIndex === hq.planetIndex ? arcBetween(centre(lead), site.surfaceDir, r) : null;
    const walk = lead && toObj !== null && toObj > 0 ? toHq(lead) / toObj : null;
    const reach = relayLink(st), linked = new Set(st.webs?.[team]?.linked ?? (st.config.relays ? linkedIds(st, team) : []));
    const webReach = st.structures.some(x => linked.has(x.id) && x.planetIndex === hq.planetIndex && arcBetween(x.surfaceDir, hq.surfaceDir, r) <= reach);
    const line = { tickets: [ours, theirs] as [number, number], defenders, strength, walk, webReach };
    const why = !lead ? 'no squad fit to strike' : ours > theirs ? 'ahead on tickets' : defenders >= strength ? 'the HQ defended' : !((walk !== null && walk <= BASE_STRIKE.walkRatio) || webReach) ? 'the HQ too far' : null;
    if (why) return { kind: 'standDown', why, line };
    return { kind: 'strike', squads: pick, structureId: hq.id, line, why: `behind or level on tickets, ${defenders} defending against ${strength}, ${webReach ? 'the web reaches it' : `the walk ${walk!.toFixed(2)}× its objective's`}` };
  };

  // the HQ strike (69.61): its proposal taken — stood down, sent, or renewed a re-plan before its task would run out
  const planStrike = (st: GameState, plan: Map<number, SquadOrder>, tick: number, dt: number) => {
    if (style !== 'adaptive' || !issue) return;
    const p = hqStrikeProposal(st, plan, tick, side);
    if (!p) return;
    const standDown = (why: string, line: StrikeLine) => {
      if (!striking) return;
      strikes.push({ tick, squadIds: striking.squadIds, structureId: striking.structureId, outcome: 'stood down', ...line, why });
      for (const id of striking.squadIds) {
        const sq = byId.get(id);
        if (sq && alive(st, sq).length && !humanOrderFor(st, sq, tick) && !standoff.holds(sq.id)) issue(sq, plan.get(id) ?? { kind: 'free' }, 'base');   // its tasks cleared
      }
      striking = null;
    };
    if (p.kind === 'standDown') { standDown(p.why, p.line); return; }
    const { squads: pick, line } = p, ids = pick.map(sq => sq.id);
    const fresh = !striking || striking.structureId !== p.structureId || ids.some(id => !striking!.squadIds.includes(id));
    if (fresh) {
      if (striking) standDown('another strike', line);
      strikes.push({ tick, squadIds: ids, structureId: p.structureId, outcome: 'sent', ...line, why: p.why });
    }
    // sent, or renewed a re-plan before its task would run out
    const renew = Math.max(1, Math.round(replanSeconds / dt));
    if (fresh || tick + renew >= striking!.until) {
      const until = tick + Math.max(1, Math.round(BASE_STRIKE.seconds / dt));
      for (const sq of pick) issue(sq, { kind: 'demolish', structureId: p.structureId }, 'task', until, undefined, { claim: 'hqStrike' });   // (69.72 step 5: rank 5, under the carry and the objective's strike)
      striking = { squadIds: ids, structureId: p.structureId, until };
    }
  };

  let heldSince: number | null = null;
  const planReserves = (st: GameState, tick: number, dt: number) => {
    const rv = reserveView(st, team);
    if (!rv) return;
    // (a squad's worth: 69.87, on the Match's squads, their mean size — the layout's 4s and 5s, not the setup's threes; not a drop's)
    const standing = own ? (st.squads ?? []).filter(q => q.team === team && !q.drop && q.unitIds.length > 0) : [];
    const size = standing.length ? Math.max(1, Math.round(standing.reduce((n, sq) => n + sq.unitIds.length, 0) / standing.length)) : squadSize;
    const enough = Math.min(size, rv.left);
    const tooLong = heldSince !== null && (tick - heldSince) * dt > RESERVE_HOLD_WAVES * personSetOf(st).respawnWave;
    // 69.6c: a point of ours being lost (under counter-attack) calls the reserve in now, and none is held back meanwhile
    const contested = counter.size > 0;
    if (rv.held && rv.waiting > 0 && (rv.waiting >= enough || tooLong || contested)) { callReserves!('call'); heldSince = null; }
    else if (!rv.held && rv.waiting > 0 && rv.waiting < enough && !contested) { callReserves!('hold'); heldSince = tick; }
  };

  // 66.4: with tactics on, a squad sent to take an enemy-held point with enemies near it gets a task on top of its
  // order — a bound from afar, a flank from nearby (the side by the squad's parity) — until it is done or expires.
  // Each is a task through `issue`, so it reaches the Match as an input like any order.
  const planTactics = (st: GameState, tick: number) => {
    const ps = personSetOf(st);
    const personLength = ps.unitRadius * 2;
    for (const [squadId, rec] of openTactic) {
      const squad = byId.get(squadId)!;
      const us = alive(st, squad);
      if (us.length === 0) { rec.outcome = 'died'; rec.closedTick = tick; openTactic.delete(squadId); continue; }
      if (tacticComplete(st, squad, rec.order, personLength, commanderArcs(st).arrive)) { rec.outcome = 'completed'; rec.closedTick = tick; openTactic.delete(squadId); continue; }
      if (tick >= rec.expires) { rec.outcome = 'expired'; rec.closedTick = tick; openTactic.delete(squadId); continue; }
      const now = current.get(squadId);
      if (!now || now.kind !== 'take' || now.pointId !== rec.order.pointId) { rec.outcome = 'superseded'; rec.closedTick = tick; openTactic.delete(squadId); }
    }
    if (!issue) return;
    const sites = st.modeState.points?.sites ?? [];
    // (182 (3), commanderPicture: defended as its side believes, not the truth)
    const enemies: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3 }> = st.config.commanderPicture !== false ? side.picture() : st.players.filter(p => p.alive && p.teamIndex !== team);
    for (const squad of squads) {
      if (openTactic.has(squad.id) || standoff.holds(squad.id) || !free(st, squad, tick)) continue;   // (69.72 step 5: none another claims — a human's order among them, which 66.4 never checked)
      const o = current.get(squad.id);
      if (!o || o.kind !== 'take') continue;
      const site = sites.find(s => s.id === o.pointId);
      if (!site || site.owner === team) continue;   // a point we do not hold (neutral or theirs) with enemies about it
      const us = alive(st, squad).filter(u => u.planetIndex === site.planetIndex);
      if (us.length < 2) continue;   // two halves at the least
      const r = st.planets[site.planetIndex].radius;
      // defended: held by the enemy, or with an enemy standing near it
      if (site.owner === null && !enemies.some(e => e.planetIndex === site.planetIndex && arcBetween(e.surfaceDir, site.surfaceDir, r) < commanderArcs(st).threat)) continue;
      const dir = squadDir(st, squad);
      if (!dir) continue;
      const d = arcBetween(dir, site.surfaceDir, r);
      const order: TacticOrder = d > 2 * commanderArcs(st).threat ? { kind: 'bound', pointId: site.id } : { kind: 'flank', pointId: site.id, side: squad.id % 2 === 0 ? 'left' : 'right' };
      const expires = tick + Math.round(TACTIC_SECONDS / PHYS_DT);
      issue(squad, order, 'task', expires);
      const rec: TacticRecord = { squadId: squad.id, order, issuedTick: tick, expires, outcome: 'open', closedTick: null };
      openTactic.set(squad.id, rec); tacticRecords.push(rec);
    }
  };

  const tacticStats = (): TacticStats => {
    const out: TacticStats = { bound: { issued: 0, completed: 0, expired: 0, died: 0 }, flank: { issued: 0, completed: 0, expired: 0, died: 0 } };
    for (const r of tacticRecords) {
      const row = out[r.order.kind];
      row.issued++;
      if (r.outcome === 'completed') row.completed++;
      else if (r.outcome === 'expired') row.expired++;
      else if (r.outcome === 'died') row.died++;
    }
    return out;
  };

  const stats = (): OrderStats => {
    const byKind: OrderStats['byKind'] = { take: { issued: 0, completed: 0, died: 0 }, hold: { issued: 0, completed: 0, died: 0 }, escort: { issued: 0, completed: 0, died: 0 }, free: { issued: 0, completed: 0, died: 0 }, fireMission: { issued: 0, completed: 0, died: 0 } };
    let completed = 0, died = 0, superseded = 0, comply = 0;
    for (const r of records) {
      if (r.order.kind === 'beacon' || r.order.kind === 'build') continue;   // 125a, 69.2b: action-clock tasks, not turn orders the counters know
      if (r.order.kind === 'move' || r.order.kind === 'engage' || r.order.kind === 'crew' || r.order.kind === 'dismount') continue;   // 68.0: no brain obeys these yet (63.1i: a dismount is no job)
      const kind = r.order.kind as keyof OrderStats['byKind'];
      if (kind === 'ride') byKind.ride ??= { issued: 0, completed: 0, died: 0 };
      if (kind === 'demolish') byKind.demolish ??= { issued: 0, completed: 0, died: 0 };   // 69.5d: the row only once one was ordered   // 63.1d step 4: the row only once a ride was ordered (stored reports keep their shape)
      byKind[kind]!.issued++;
      if (r.outcome === 'completed') { completed++; byKind[kind]!.completed++; comply += (r.closedTick! - r.issuedTick); }
      else if (r.outcome === 'died') { died++; byKind[kind]!.died++; }
      else if (r.outcome === 'superseded') superseded++;
    }
    return { issued: records.length, completed, died, superseded, meanComplySeconds: completed ? comply / completed : NaN, byKind, ...(tactics ? { tactics: tacticStats() } : {}) };
  };

  return {
    team, style, records,
    get squads() { return squads; },
    get trace() { return cmd.trace; },
    set trace(f) { cmd.trace = f; },
    proposeHqStrike: (st, plan, tick) => hqStrikeProposal(st, plan, tick, sideRead(st, team, tick)),
    proposeCarry: (st, tick) => carryProposal(st, tick),
    orderFor: (unitId) => { if (style === 'none') return null; const sq = byUnit.get(unitId); return sq === undefined ? null : (current.get(sq) ?? { kind: 'free' }); },
    step, stats, gunSupports, strikes, refusals, searches,
    get ambushes() { return ambush.records(); },
    get rallies() { return rally.records(); },
  };
}

/**
 * 69.73 step 2b: a planet of radius `r` cut into search cells about `spacing` apart (world units) — the directions of a
 * Fibonacci sphere of as many points as the planet's surface holds cells, in index order. Deterministic (dmath).
 */
export function searchCells(r: number, spacing: number): Vec3[] {
  const n = Math.max(8, Math.round((4 * Math.PI * r * r) / (spacing * spacing)));
  const golden = Math.PI * (3 - Math.sqrt(5));
  const out: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const y = 1 - (2 * (i + 0.5)) / n, ring = Math.sqrt(Math.max(0, 1 - y * y)), a = golden * i;
    out.push({ x: ring * dcos(a), y, z: ring * dsin(a) });
  }
  return out;
}

/** `meanComplySeconds` needs dt; callers convert ticks with PHYS_DT. Exposed for the harness. */
export function complySeconds(stats: OrderStats, dt: number): number { return stats.meanComplySeconds * dt; }

/** 69.2e: the direction `angle` radians along the great circle from `a` toward `b`. */
function stepToward(a: Vec3, b: Vec3, angle: number): Vec3 {
  const u = vecNormalize(a), v = vecNormalize(b);
  const t = vecSub(v, { x: u.x * vecDot(u, v), y: u.y * vecDot(u, v), z: u.z * vecDot(u, v) });
  const len = vecLength(t);
  if (len < 1e-12) return u;
  const w = { x: t.x / len, y: t.y / len, z: t.z / len };
  const c = dcos(angle), sn = dsin(angle);
  return vecNormalize({ x: u.x * c + w.x * sn, y: u.y * c + w.y * sn, z: u.z * c + w.z * sn });
}
